"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { flushSync } from "react-dom"
import { usePathname, useRouter } from "next/navigation"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"

import { toast } from "sonner"
import { useAuth } from "@/contexts/AuthContext"
import { useAnalyticsUserTier } from "@/hooks/use-analytics-user-tier"
import { validateField, VALIDATION_RULES } from "@/lib/form-validation"
import { verifyAnalysis } from "@/lib/verify-analysis-client"
import {
  analytics,
  trackEvent,
  performanceAnalytics,
  getPosthogDistinctId,
  analysisFunnelEnvelope,
  surfaceFromPathname,
  type AnalysisGateReason,
} from "@/lib/analytics"
import {
  anonymousLimitImpliesEmailTier,
  markEmailUnlockHint,
  resolveAnalyticsUserTier,
} from "@/lib/analytics/user-tier"
import { ensureAnalysisFunnelId } from "@/lib/analysis-funnel-id"
import { getCurrentSeason } from "@/lib/date-utils"
import { compressImage, formatFileSize, ImageCompressionError } from "@/lib/image-compression"
import { isTransientNetworkError } from "@/lib/is-transient-network-error"
import {
  trackUploadPrecheckRejected,
  UPLOAD_REJECTION_COPY,
  type UploadPrecheckReason,
} from "@/lib/upload-rejection"
import {
  clearAnonQuotaReason,
  readAnonQuotaReason,
  writeAnonQuotaReason,
  type AnonQuotaReason,
} from "@/lib/anon-quota-flag"
import { safeJson, errorMessageFrom } from "@/lib/safe-json"
import { detectEmailTypo, type EmailSuggestion } from "@/lib/email-typo-detection"
import { buildTimelineUrl } from "@/lib/timeline-url"
import { claimAnalysisOnce, wasJustAuthed } from "@/lib/claim-analysis"
import { clearBreadcrumb, writeBreadcrumb } from "@/hooks/use-last-analysis-breadcrumb"
import { UPLOAD_ACCEPT, UPLOAD_MAX_BYTES_HOMEPAGE } from "@/lib/upload-constants"
import { consumePendingScanFile } from "@/lib/pending-scan-file"
import { useFlagExposure, useSilentFlagVariant } from "@/hooks/use-experiment-flag"
import { readSavedZip } from "@/hooks/use-saved-inputs"
import { WEB_SKIP_ZIP_FLAG, isSkipZipTreatment, readSkipZipAssignment, saveSkipZipAssignment } from "@/lib/web-skip-zip"
import { prefetchIpZip, ZIP_IP_PREFILL_FLAG } from "@/lib/ip-zip-prefill"
import { isTrustedAnalysisImageUrl as isTrustedAnalysisImageUrlShared } from "@/lib/image-url-validation"
import { TIER_PHOTO_CAPS, type PhotoLimitDetail, type PhotoLimitReason, type PhotoTier } from "@/lib/analysis/photo-caps"
import {
  REFINEMENT_GUIDANCE_CONFLICT,
  REFINEMENT_GUIDANCE_FALLBACK,
  isRefinementTrigger,
  resolveNextPhotoGuidance,
  type RefinementPolicy,
  type RefinementTrigger,
} from "@/lib/analysis/refinement-policy"
import type { AnalysisMode as SharedAnalysisMode, AnalysisQualityEnvelope, DiagnosisResult } from "@/lib/types/diagnosis"
import type { WeedIdentificationResult } from "@/lib/types/weed-identification"
import type { IdentifyAbstentionReason } from "@/lib/analysis/abstention"
import { measurePhotoQuality } from "@/lib/analysis/measure-photo-quality"
import { clearPendingOAuthRedirect, isOAuthRedirectReplacing } from "@/lib/auth/oauth-pending-redirect"
import { readScanRecovery, saveScanRecovery, SCAN_RECOVERY_KEY } from "@/lib/scan-recovery"
import type { PhotoQualityMetrics, PhotoQualityReason } from "@/lib/analysis/photo-quality"
import {
  photoQualityAnalyticsProps,
  photoQualityMeasurementProps,
  type PhotoRole,
} from "@/lib/analysis/photo-quality-analytics"

type AnalysisMode = Exclude<SharedAnalysisMode, 'weed'>

export interface AnalysisResult {
  grassType: string
  subjectGuess?: 'grass' | 'weed' | 'other_plant' | 'not_plant' | 'unreadable'
  abstentionReason?: IdentifyAbstentionReason | null
  identificationStatus?: 'identified' | 'abstained'
  missingEvidence?: string[]
  recommendedNextPhoto?: string | null
  confidence: number
  /** Server-gated honest confidence (margin/image/region caps). Display
      surfaces read `displayConfidence ?? confidence`; raw confidence is kept
      for analytics + the future calibration fit. */
  displayConfidence?: number
  /** Top-1..3 model probability distribution (sanitized server-side). */
  candidates?: { name: string; probability: number }[]
  /** Working matches for an abstained grass scan. A plan needs a user choice. */
  recovery?: {
    version: 'identify-recovery-v1'
    status: 'ranked' | 'unavailable'
    comparisonAttempted: boolean
    comparisonOutcome: 'accepted' | 'regional_fallback' | 'skipped_deadline' | 'failed' | 'rejected_output'
    recommendedGrass: string | null
    recommendationSource: 'visual_comparison' | 'regional_prior' | null
    candidates: {
      name: string
      rank: number
      source: 'visual_comparison' | 'regional_prior'
      cue: string | null
    }[]
    comparisonSummary: string | null
  }
  /** Closest weed match when subjectGuess='weed' (data/weed-types.ts key). */
  weedGuess?: string | null
  /** Visually distinct secondary weeds in an otherwise turf-focused photo. */
  possibleWeeds?: { name: string; confidence: number; evidence: string }[]
  visibleEvidence?: string[]
  quality?: AnalysisQualityEnvelope
  tips: string[]
  enhancedTips?: string[]
  region: string
  image: string
  alternatives?: string[]
  analysis?: string
  seasonalNotes?: string
  imageQuality?: string
  model?: string
  promptStrategy?: string
  pipelineVersion?: string
  /** A user correction is identity provenance, not model confidence. */
  identitySource?: 'model' | 'user_selected' | 'user_confirmed' | 'expert_verified'
  originalGrassType?: string
  /** The score the model gave `originalGrassType`. Never reassigned to the
   *  species the user picked; it exists so the original prediction can be
   *  rendered as labeled history without re-reading the live score. */
  originalConfidence?: number
  selectedAt?: string
}

export interface SupportingAnalysisImage {
  url: string
  preview: string
}

/**
 * A soft, pre-call capture warning. Advisory only: it never blocks the upload
 * and never gates the analyze button. Absent whenever the client-side check
 * could not judge the photo, which is the fail-open default.
 */
export interface PhotoQualityWarning {
  reason: PhotoQualityReason
  metrics: PhotoQualityMetrics
  role: PhotoRole
  /** The upload attempt this verdict belongs to. */
  attemptId: string
  measureMs: number
}

/**
 * Cross-platform outcome vocabulary for `refinement_outcome_delta`. Mobile
 * fires the same five values so PostHog can union web and app.
 */
export type AnalysisOutcomeLabel =
  | 'identified'
  | 'low_confidence'
  | 'unknown_other'
  | 'abstained'
  | 'failed'

const ANALYSIS_OUTCOME_LABELS: readonly AnalysisOutcomeLabel[] = [
  'identified',
  'low_confidence',
  'unknown_other',
  'abstained',
  'failed',
]

export function isAnalysisOutcomeLabel(value: unknown): value is AnalysisOutcomeLabel {
  return typeof value === 'string' && (ANALYSIS_OUTCOME_LABELS as readonly string[]).includes(value)
}

/** What the analyze route denied, and which lane closes the gap. */
export interface PhotoCapGateState {
  /** 'photo_cap' = the server rejected the photo set. 'refinement' = the user
   *  tapped a refinement CTA while signed out, so nothing was sent yet. */
  intent: 'photo_cap' | 'refinement'
  requiredTier: Exclude<PhotoTier, 'anonymous'>
  mode: AnalysisMode
  cap: number
  requested: number
  reason: RefinementTrigger | null
  /** Which 403 the server sent, when one did. 'additive_cap' means the request
   *  was inside the photo cap and the free refinement allowance behind it is
   *  spent, so the photo-count copy would be false. Absent for gates the client
   *  opens on its own (a refinement tap). */
  denialReason?: PhotoLimitReason | null
}

/** Drives the "Refined with N photos" confirmation on a re-run result. */
export interface RefinementSummary {
  imageCount: number
  previousOutcome: AnalysisOutcomeLabel
  newOutcome: AnalysisOutcomeLabel
  previousConfidence: number | null
  newConfidence: number | null
}

function identifyOutcomeLabel(result: AnalysisResult | null): AnalysisOutcomeLabel {
  if (!result) return 'failed'
  if (result.identificationStatus === 'abstained' || result.abstentionReason) return 'abstained'
  if (result.grassType === 'Unknown/Other') return 'unknown_other'
  const effective = result.displayConfidence ?? result.confidence
  if (typeof effective !== 'number' || effective <= 0) return 'failed'
  if (effective < 50) return 'low_confidence'
  return 'identified'
}

function diagnoseOutcomeLabel(result: DiagnosisResult | null): AnalysisOutcomeLabel {
  if (!result) return 'failed'
  if (result.quality?.status === 'abstained' || result.outcome === 'inconclusive') return 'abstained'
  return 'identified'
}

export interface AnalysisLawnContext {
  lawnId?: string
  knownGrassType?: string
  grassTypeProvenance?: 'user_confirmed' | 'expert_verified' | 'model_predicted' | 'region_inferred' | 'saved_lawn'
  lawnContextIntent?: 'automatic_single' | 'user_selected' | 'route_locked'
}

export interface InitialSavedAnalysis {
  analysisId: string
  lawnId?: string | null
  zipCode: string
  imageUrl: string | null
  mode: AnalysisMode
  result?: AnalysisResult | null
  diagnosis?: DiagnosisResult | null
  resultAccessToken?: string | null
}

export interface RecoveredWeedAnalysis {
  result: WeedIdentificationResult
  analysisId: string
  resultAccessToken: string
  /** The `weed_recovery_*` attempt the recovery call itself reported. The
      rendered weed result must key its impressions to this, not to the parent
      identify attempt, or the recovery events cannot be joined to the result. */
  attemptId: string
}

interface HistoryStatePayload {
  savedAnalysisId: string | null
  analysisResult: AnalysisResult | null
  diagnosisResult: DiagnosisResult | null
  zipCode: string
  uploadedImageUrl: string | null
  supportingImages: SupportingAnalysisImage[]
  isUploadingSupportingImages: boolean
  supportingImageError: string
  analysisMode: AnalysisMode
  resultAccessToken: string | null
}

// Kill switch — set `NEXT_PUBLIC_HOMEPAGE_PUSHSTATE_DISABLED=true` to fall
// back to the original replaceState-only behavior if pushState/popstate
// turn out to interact badly with the OAuth resume lane in production.
const PUSHSTATE_DISABLED =
  process.env.NEXT_PUBLIC_HOMEPAGE_PUSHSTATE_DISABLED === 'true'

// Accuracy-first image upload treatment. This is deliberately a deploy-time
// kill switch with a bounded 4 MB / 3072 px target, not an unlimited-original
// upload path. Files already under the target remain byte-for-byte unchanged.
const HIGH_FIDELITY_IMAGE_UPLOADS =
  process.env.NEXT_PUBLIC_GRASS_ID_HIGH_FIDELITY === 'true'

const LEGACY_IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1920,
  quality: 0.85,
} as const

const HIGH_FIDELITY_PRIMARY_OPTIONS = {
  maxSizeMB: 4,
  maxWidthOrHeight: 3072,
  quality: 0.92,
  minQuality: 0.75,
} as const

const HIGH_FIDELITY_SUPPORTING_OPTIONS = {
  maxSizeMB: 2.5,
  maxWidthOrHeight: 2560,
  quality: 0.88,
  minQuality: 0.78,
} as const

type PreparedGrassImage = {
  result: Awaited<ReturnType<typeof compressImage>>
  fidelityFallbackReason?: string
}

async function prepareGrassImage(
  file: File,
  role: 'primary' | 'supporting',
): Promise<PreparedGrassImage> {
  if (!HIGH_FIDELITY_IMAGE_UPLOADS) {
    return { result: await compressImage(file, LEGACY_IMAGE_COMPRESSION_OPTIONS) }
  }

  const treatmentOptions = role === 'primary'
    ? HIGH_FIDELITY_PRIMARY_OPTIONS
    : HIGH_FIDELITY_SUPPORTING_OPTIONS
  try {
    return { result: await compressImage(file, treatmentOptions) }
  } catch (error) {
    // A hard output-size miss can recover under the proven 1920 px profile.
    // Never fall back to the raw original, and keep the same attempt id at the
    // caller so one user action remains one measurable upload attempt.
    if (
      error instanceof ImageCompressionError &&
      (error.code === 'OUTPUT_LIMIT_UNMET' || error.code === 'COMPRESSION_FAILED')
    ) {
      return {
        result: await compressImage(file, LEGACY_IMAGE_COMPRESSION_OPTIONS),
        fidelityFallbackReason: error.code,
      }
    }
    throw error
  }
}

interface CanonicalUploadMetadata {
  inputBytes: number
  outputBytes: number
  inputWidth: number | null
  inputHeight: number | null
  outputWidth: number
  outputHeight: number
  outputFormat: 'jpeg'
  wasResized: boolean
  serverQuality: number
}

// Upload-route rejections that mean "this photo can't be used", not "the app is
// broken": app/api/upload/image/route.ts already telemeters every one of them
// server-side (serverAnalytics.imageSanitizationRejected / imageProcessingFailed).
// Capturing them client-side as well filed ONE bad photo as TWO Error Tracking
// issues — the server VipsJpeg exception plus a client Error carrying the same
// friendly 422 body, second-for-second. (PostHog issue 01a016b2, 2026-08-21.)
// Expected-input only: 5xx, missing bodies, and network failures keep their
// exception capture, including the 'Upload failed (<status>)' 500 canary.
const EXPECTED_UPLOAD_REJECTION_CODES = new Set([
  // 422 — sharp/libvips could not safely decode or re-encode the photo.
  'image_dimensions_too_large',
  'processed_image_too_large',
  'image_processing_failed',
  // 400 — pre-decode validation of the submitted file.
  'no_file_provided',
  'invalid_file_type',
  'file_too_large',
  'invalid_image_signature',
])

function isExpectedUploadRejection(
  context: Record<string, string | number | boolean | undefined> | undefined
): boolean {
  if (!context) return false
  const status = context.http_status
  const code = context.server_error_code
  // Both statuses require an allowlisted code: a 422/400 whose body lacks a
  // known code did not come from the route's expected-rejection paths, so it
  // stays on the exception lane rather than being silently absorbed.
  return (
    (status === 422 || status === 400) &&
    typeof code === 'string' &&
    EXPECTED_UPLOAD_REJECTION_CODES.has(code)
  )
}

/** Server faults (>=500) whose cause is known and whose remedy is the user's
 *  to act on. Everything else at 5xx is an unhandled fault and keeps the
 *  generic line, so an internal detail can never reach a toast. Values mirror
 *  the sentences `app/api/ai/analyze-grass/route.ts` returns for these codes. */
const SERVER_FAULT_COPY: Record<string, string> = {
  usage_check_failed: 'Unable to verify scan limits right now. Please try again shortly.',
  lawn_lookup_failed: 'We could not verify that lawn right now. Please try again in a moment.',
  openai_upstream_unavailable: 'AI service temporarily unavailable. Please try again in a moment.',
  openai_api_error: 'The AI could not finish this analysis. Please try again in a moment.',
  analysis_timeout: 'The analysis took too long to finish. Please try again with the same photo.',
}

export function serverFaultMessage(
  httpStatus: number | undefined,
  serverErrorCode: string | undefined,
): string | null {
  if (typeof httpStatus !== 'number' || httpStatus < 500) return null
  if (!serverErrorCode) return null
  return SERVER_FAULT_COPY[serverErrorCode] ?? null
}

const RESCAN_ANALYSIS_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ANALYSIS_RESUME_ID_RE = /^[a-zA-Z0-9_-]{8,120}$/
/** Opaque result-access token carried through the OAuth round trip so a
 *  signed-out visitor's refinement can still prove ownership of its root row. */
/** `<analysisId>.<expiresAt>.<signature>` — see lib/analysis/result-access-token. */
const RESULT_ACCESS_TOKEN_RE = /^[a-zA-Z0-9-]{8,64}\.\d{1,15}\.[A-Za-z0-9_-]{16,128}$/

function newAnalysisResumeId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `resume_${crypto.randomUUID().replace(/-/g, '_')}`
    }
  } catch { /* noop */ }
  return `resume_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}


export interface UseHomepageAnalysisOptions {
  /**
   * When provided, bootstrap state from a saved analysis row instead of an
   * empty initial state. Used by the SSR `/result/[id]` page so navigating
   * to a saved-result URL renders the same hybrid layout as a fresh
   * identification. Takes precedence over URL-param-based restoration.
   */
  initialSavedAnalysis?: InitialSavedAnalysis
  /**
   * When the user clicks OAuth on the upgrade card, the roundtrip returns
   * here. Defaults to the homepage with `?resume=1&imageUrl=&zipCode=&mode=`
   * params (which the hook reads on mount to rehydrate an in-flight flow).
   * Pass an explicit path (e.g. `/result/<id>`) on pages where the analysis
   * is already saved server-side and resume params are unnecessary.
   */
  oauthResumePath?: string
  /**
   * Default analysis mode for the page hosting the hook. The `/diagnose`
   * landing page passes 'diagnose'. URL `?mode=` and initialSavedAnalysis
   * still take precedence.
   */
  initialMode?: AnalysisMode
  /** Explicit or unambiguous lawn context for this analysis. Saved-lawn
      provenance is useful as a prior but is intentionally not treated as
      verified chemical safety without server-owned confirmation. */
  lawnContext?: AnalysisLawnContext
}

export function useHomepageAnalysis(options?: UseHomepageAnalysisOptions) {
  // Hooks (must be called before any conditional logic or state that depends on them)
  const {
    user,
    loading: authLoading,
    isPremium: authIsPremium,
    subscriptionLoading: subscriptionPending,
    subscriptionError,
  } = useAuth()
  // Entitlement is "unknown" while the lookup is in flight AND when it failed:
  // a 500 leaves isPremium false, which is an absence of an answer, not a
  // negative one. Paid surfaces read this and fail closed on either.
  const subscriptionLoading = subscriptionPending || subscriptionError
  const router = useRouter()
  const searchParams = usePrerenderSafeSearchParams()
  const pathname = usePathname()

  // The monetization rung every funnel envelope emitted from this page carries.
  // Set once here rather than threaded through thirty envelope call sites; see
  // useAnalyticsUserTier. `emailTierObserved` is the signed-out middle rung:
  // EMAIL_UNLOCK_COOKIE is httpOnly + signed, so the only way a browser learns
  // it holds one is the server telling it, which the analyze route does through
  // the 429 `tier` field and through the anonymous ceiling on a success payload.
  const [emailTierObserved, setEmailTierObserved] = useState(false)
  useAnalyticsUserTier(emailTierObserved)

  // Image state
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(
    options?.initialSavedAnalysis?.imageUrl ?? null,
  )
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState("")
  const [supportingImages, setSupportingImages] = useState<SupportingAnalysisImage[]>([])
  const [isUploadingSupportingImages, setIsUploadingSupportingImages] = useState(false)
  const [supportingImageError, setSupportingImageError] = useState("")
  const [isRefiningWithSupportingPhoto, setIsRefiningWithSupportingPhoto] = useState(false)
  const [supportingPhotoGuidance, setSupportingPhotoGuidance] = useState<string | null>(null)
  // Client-side capture-quality verdicts. Advisory only — nothing here can
  // block an upload or disable analyze.
  const [photoQuality, setPhotoQuality] = useState<PhotoQualityWarning | null>(null)
  const [supportingPhotoQuality, setSupportingPhotoQuality] =
    useState<Record<string, PhotoQualityReason>>({})
  // Staleness guard for the fire-and-forget measurement. Deliberately NOT
  // attemptIdRef: that ref is only assigned once the upload succeeds, so a fast
  // measurement resolving first would compare against the previous attempt and
  // be dropped.
  const photoQualityTokenRef = useRef<string | null>(null)
  // Supporting-side staleness guard, the counterpart of photoQualityTokenRef:
  // a resolving measurement only lands if its URL is still part of the current
  // supporting set. Added at upload time (state lands later, so the state list
  // cannot be the guard), pruned on remove/reset/refinement-trim.
  const supportingQualityUrlsRef = useRef<Set<string>>(new Set())
  // Impression dedup for supporting capture_quality_warning_shown events.
  const supportingQualityShownKeysRef = useRef<Set<string>>(new Set())
  // Server's answer to "is this result refinable, by whom, with how many
  // photos". Null until the first response, and on saved-result pages that
  // never call analyze — every consumer keeps a local fallback for that case.
  const [refinementPolicy, setRefinementPolicy] = useState<RefinementPolicy | null>(null)
  // Non-null while a photo-cap denial or a signed-out refinement tap is being
  // shown. Deliberately separate from limitReached/upgradeRequired: those are
  // monthly-quota state and their copy is wrong for a photo-count denial.
  const [photoCapGate, setPhotoCapGate] = useState<PhotoCapGateState | null>(null)
  const [refinementSummary, setRefinementSummary] = useState<RefinementSummary | null>(null)
  // Analytics label for the CTA that opened the current refinement. Never sent
  // as an entitlement claim — the server derives additive-vs-replacement.
  const refinementTriggerRef = useRef<RefinementTrigger | null>(null)
  // Captured before refineWithSupportingPhoto clears result state, consumed by
  // the next successful run to emit the outcome delta.
  // A saved-result mount seeds it from the row it rendered, so the /result/:id
  // hop into the refinement lane carries an outcome the delta can be measured
  // against. A homepage mount has no saved row and starts null as before.
  const previousOutcomeRef = useRef<{
    outcome: AnalysisOutcomeLabel
    confidence: number | null
  } | null>((() => {
    const saved = options?.initialSavedAnalysis
    if (saved?.diagnosis) {
      return {
        outcome: diagnoseOutcomeLabel(saved.diagnosis),
        confidence: saved.diagnosis.quality?.displayConfidence ?? saved.diagnosis.confidence ?? null,
      }
    }
    if (saved?.result) {
      return {
        outcome: identifyOutcomeLabel(saved.result),
        confidence: saved.result.displayConfidence ?? saved.result.confidence ?? null,
      }
    }
    return null
  })())
  // How many photos this visitor may send right now. The server's answer wins
  // once we have one; before that, the tier ladder's own defaults stand in so
  // the very first upload already renders the right number of slots.
  const photoCap = refinementPolicy?.photoCap
    ?? (user ? TIER_PHOTO_CAPS.registered_free : TIER_PHOTO_CAPS.anonymous)
  const supportingPhotoSlots = Math.max(0, photoCap - 1)

  // Restore state from URL params (survives OAuth redirects on all platforms).
  // When initialSavedAnalysis is provided, the SSR row is authoritative and we
  // ignore URL params so the OAuth resume lane can't conflict with the
  // rehydrated state on `/result/<id>` (e.g. a leftover `?resume=1` from a
  // prior session).
  const skipUrlRestoration = Boolean(options?.initialSavedAnalysis)
  const resumeFromUrl = !skipUrlRestoration && searchParams.get('resume') === '1'
  const recoveryReturnId = skipUrlRestoration ? null : searchParams.get('scanRecovery')
  const [recoveryId, setRecoveryId] = useState('')
  const recoveryRestoredRef = useRef(false)
  const urlImageUrl = skipUrlRestoration ? null : searchParams.get('imageUrl')
  const urlSupportingImageUrls = skipUrlRestoration
    ? []
    : searchParams.getAll('supportingImageUrl')
  const urlZipCode = skipUrlRestoration ? null : searchParams.get('zipCode')
  const urlRescanOfAnalysisId = skipUrlRestoration ? null : searchParams.get('rescanOfAnalysisId')
  const resumeAnalysisAfterAuth = !skipUrlRestoration && searchParams.get('resumeAnalysis') === '1'
  // Ownership proof for a refinement that crossed OAuth. Without it the server
  // cannot match the new account to the anonymous root row, and the free
  // refinement the sign-in gate promised would spend a monthly scan instead.
  const urlSourceResultAccessToken = (() => {
    const value = skipUrlRestoration ? null : searchParams.get('sourceResultAccessToken')
    return value && RESULT_ACCESS_TOKEN_RE.test(value) ? value : null
  })()
  const urlRefinementTrigger = (() => {
    const value = skipUrlRestoration ? null : searchParams.get('refinementTrigger')
    return isRefinementTrigger(value) ? value : null
  })()
  // The outcome the visitor was refining AWAY from. previousOutcomeRef is React
  // memory and dies in the OAuth redirect, so without these two params every
  // refinement that crossed sign-in landed with no refinement_outcome_delta:
  // the exact path the gate exists for was invisible to its own success metric.
  const urlRefinementPreviousOutcome = (() => {
    const value = skipUrlRestoration ? null : searchParams.get('refinementPrevOutcome')
    return isAnalysisOutcomeLabel(value) ? value : null
  })()
  const urlRefinementPreviousConfidence = (() => {
    const value = skipUrlRestoration ? null : searchParams.get('refinementPrevConfidence')
    if (value === null || !/^\d{1,3}$/.test(value)) return null
    const parsed = Number(value)
    return parsed >= 0 && parsed <= 100 ? parsed : null
  })()
  const urlAnalysisResumeId = (() => {
    const value = skipUrlRestoration ? null : searchParams.get('analysisResumeId')
    return value && ANALYSIS_RESUME_ID_RE.test(value) ? value : null
  })()

  // Mode state — initialSavedAnalysis takes precedence over `?mode=diagnose`,
  // which takes precedence over the page-level default (options.initialMode).
  const initialMode =
    options?.initialSavedAnalysis?.mode
    ?? (searchParams.get('mode') === 'diagnose' ? ('diagnose' as const) : null)
    ?? options?.initialMode
    ?? 'identify'
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>(initialMode)

  // Analysis state — bootstrap from the saved row when provided. The
  // savedAnalysisId initialization is what triggers the URL-rewrite and
  // analytics-observer effects to treat this page as a rendered result.
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(
    options?.initialSavedAnalysis?.result ?? null,
  )
  const [diagnosisResult, setDiagnosisResult] = useState<DiagnosisResult | null>(
    options?.initialSavedAnalysis?.diagnosis ?? null,
  )
  const [savedAnalysisId, setSavedAnalysisId] = useState<string | null>(
    options?.initialSavedAnalysis?.analysisId ?? null,
  )
  const [resultAccessToken, setResultAccessToken] = useState<string | null>(
    options?.initialSavedAnalysis?.resultAccessToken ?? urlSourceResultAccessToken ?? null,
  )
  const [recoveredWeed, setRecoveredWeed] = useState<RecoveredWeedAnalysis | null>(null)
  const [isRecoveringWeed, setIsRecoveringWeed] = useState(false)
  const [weedRecoveryFailed, setWeedRecoveryFailed] = useState(false)
  const [limitReached, setLimitReached] = useState(false)
  // Which rung of the anonymous ladder the walled visitor is on, echoed by the
  // analyze route's 429. 'email' means they already gave us an address and
  // spent the higher tier, so the wall must not re-ask for one.
  const [limitTier, setLimitTier] = useState<'anon' | 'email'>('anon')
  // Scan quota from the analyze route's success payload (anon: 3/month).
  const [scanUsage, setScanUsage] = useState<{
    remaining: number
    limit: number
    isPremium: boolean
  } | null>(null)
  const [upgradeRequired, setUpgradeRequired] = useState(false)
  const [quotaResumeId, setQuotaResumeId] = useState<string | null>(urlAnalysisResumeId)
  const [quotaResumePending, setQuotaResumePending] = useState(resumeAnalysisAfterAuth)
  // Why THIS device's anonymous quota is spent for the current calendar month,
  // or null when it isn't. `blocked` means an attempt was actually walled
  // (signupRequired) — those users get the pre-upload notice so the wall isn't
  // sprung after the photo + ZIP effort. `consumed` means a run SUCCEEDED on
  // the last free slot; nothing is walled yet, so that population gets no
  // notice (see lib/anon-quota-flag). Cleared when the month rolls over, when a
  // later run succeeds with quota remaining, or when the user signs in.
  const [anonQuotaReason, setAnonQuotaReason] = useState<AnonQuotaReason | null>(null)
  const anonQuotaExhausted = anonQuotaReason !== null
  // Set when the analyze fetch is killed by a transient network failure rather
  // than a real server error — the dominant case is iOS Safari aborting the
  // in-flight request when the tab is backgrounded mid-call (surfaces as
  // `TypeError: Load failed`), plus flaky-connection drops. Distinct from the
  // generic "Analysis Failed" result so the UI can offer a one-tap retry that
  // re-runs against the already-uploaded image (no re-pick). Cleared on retry,
  // reset, a new upload, and at the start of each analyze run.
  const [analysisNetworkError, setAnalysisNetworkError] = useState(false)
  // A completed analysis can still fail while its durable row is saved. Keep
  // the photo and ZIP in place so the user can retry without starting over.
  // The server refunds anonymous quota before it returns this error.
  const [analysisSaveError, setAnalysisSaveError] = useState(false)
  // The server failed or timed out before returning a result (5xx, non-JSON
  // body, or an unusable response). The photo and ZIP stay in place for a
  // same-photo retry; every such server path refunds anonymous quota.
  const [analysisServerError, setAnalysisServerError] = useState(false)

  // Rehydrate the anon-quota flag for the active mode. Month-scoped so the
  // notice disappears on its own when the server-side quota resets.
  useEffect(() => {
    setAnonQuotaReason(readAnonQuotaReason(analysisMode))
  }, [analysisMode])

  // ZIP code state — same precedence: saved row first, then URL params.
  const [zipCode, setZipCode] = useState(
    options?.initialSavedAnalysis?.zipCode
      || (resumeFromUrl && urlZipCode ? urlZipCode : ""),
  )
  const [zipCodeError, setZipCodeError] = useState("")
  const [isZipCodeValid, setIsZipCodeValid] = useState(false)
  const skipZipVariant = useSilentFlagVariant(WEB_SKIP_ZIP_FLAG)
  const [skipZipAssignment, setSkipZipAssignment] = useState<boolean | null>(null)
  useEffect(() => { setSkipZipAssignment(readSkipZipAssignment()) }, [])
  const skipZipStep = skipZipAssignment ?? isSkipZipTreatment(skipZipVariant)
  const autoStartPhotoRef = useRef<string | null>(null)
  useFlagExposure(WEB_SKIP_ZIP_FLAG, Boolean(uploadedImageUrl) && skipZipVariant !== undefined
    && skipZipAssignment === isSkipZipTreatment(skipZipVariant))

  // zip_ip_prefill_v1: read silently here only to start the IP ZIP lookup at
  // upload time. Exposure and the fill itself belong to ZipCodeStep, where the
  // field is visible.
  const zipIpPrefillVariant = useSilentFlagVariant(ZIP_IP_PREFILL_FLAG)
  const zipIpPrefillVariantRef = useRef(zipIpPrefillVariant)
  useEffect(() => {
    zipIpPrefillVariantRef.current = zipIpPrefillVariant
  }, [zipIpPrefillVariant])

  // Per-attempt id: generated on each photo upload, threaded through every
  // analytics event in that run (photo_uploaded → zip_valid_entered →
  // analysis_started → completed/failed/limited → result_viewed → retry).
  // Lets repeat uploads in a single session be grouped as discrete attempts
  // in PostHog, which is the multi-upload pattern we want to understand.
  // When the hook is bootstrapped from a saved analysis row, prefix the id
  // with `saved:` so PostHog funnels can separate the saved-result cohort
  // (no upload/zip/started events fire) from fresh identifications.
  const attemptIdRef = useRef<string | null>(
    options?.initialSavedAnalysis
      ? `saved:${options.initialSavedAnalysis.analysisId}`
      : null,
  )
  const funnelIdRef = useRef<string | null>(null)
  const analysisLawnContextRef = useRef<AnalysisLawnContext>({
    lawnId: options?.initialSavedAnalysis?.lawnId ?? options?.lawnContext?.lawnId,
    knownGrassType: options?.lawnContext?.knownGrassType,
    grassTypeProvenance: options?.lawnContext?.grassTypeProvenance,
    lawnContextIntent: options?.lawnContext?.lawnContextIntent,
  })
  const analysisResumeFlowRef = useRef<{
    id: string
    mode: AnalysisMode
    entryPath: string
  } | null>(urlAnalysisResumeId ? {
    id: urlAnalysisResumeId,
    mode: initialMode,
    entryPath: pathname,
  } : null)
  // Self-reference for analyzeImage's one deferred auto-retry. Calling the
  // callback from inside its own body would make its type circular, so the
  // scheduled retry goes through this ref instead.
  const analyzeImageRef = useRef<
    ((modeOverride?: AnalysisMode | unknown, lawnContextOverride?: AnalysisLawnContext) => Promise<void>) | null
  >(null)
  // Resume flow id whose analyze run already burned its one automatic retry.
  // Keyed by id (not a boolean) so a later, distinct resume gets its own single
  // retry while the current one can never loop.
  const autoRetriedResumeIdRef = useRef<string | null>(null)
  // Last attempt id for which we already fired grass_id_result_viewed.
  // Prevents re-firing on re-renders while the result is on screen.
  const resultViewedFiredForRef = useRef<string | null>(null)
  // Same dedupe pattern for the hybrid-layout scroll-funnel events. Separate
  // refs keyed to attempt id so a scroll-out/scroll-in cycle in the same
  // attempt cannot double-fire.
  const planPreviewScrolledFiredForRef = useRef<string | null>(null)
  const upgradePromptedFiredForRef = useRef<string | null>(null)
  const productsScrolledFiredForRef = useRef<string | null>(null)
  const toolsScrolledFiredForRef = useRef<string | null>(null)
  // Single-flight guard around analyzeImage so Enter-then-click (or any
  // double-submit race) cannot kick off two API calls.
  const analyzeInFlightRef = useRef<boolean>(false)
  // A real retry from the results UI calls resetUpload() first, then uploads a
  // new file. Preserve that intent so the next successful upload can link back
  // to the previous attempt instead of losing context during reset.
  const pendingRetryRef = useRef<{
    previousAttemptId: string | null
    fromResult: boolean
    mode: AnalysisMode
  } | null>(null)
  // A result retry must keep the original row id even though resetUpload clears
  // visible result state. The next new-photo analysis sends this id to unlock
  // the server's linked free-rescan path. It survives failed retries and clears
  // only after a successful result.
  const rescanOfAnalysisIdRef = useRef<string | null>(
    urlRescanOfAnalysisId && RESCAN_ANALYSIS_ID_RE.test(urlRescanOfAnalysisId)
      ? urlRescanOfAnalysisId
      : null,
  )
  // True once the URL restore below has actually applied a trusted photo. The
  // localStorage restore reads this instead of the query string: the query is
  // scrubbed right after restoring, and that effect re-runs when auth settles,
  // so a param check would let a later run rehydrate the stale `_flow` copy.
  const urlRestoredPhotoRef = useRef(false)
  const primaryImageMetricsRef = useRef<{
    originalSizeKb?: number
    uploadedSizeKb?: number
    width?: number
    height?: number
  }>({})

  // Generate a new uuid using crypto when available; falls back to a
  // collision-resistant pseudo-id for environments without crypto.randomUUID.
  const newAttemptId = useCallback((): string => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
      }
    } catch { /* noop */ }
    return `att_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
  }, [])

  useEffect(() => { setRecoveryId(newAttemptId()) }, [newAttemptId])

  // Delegates to lib/analysis-funnel-id so UploadCard can mint the same id when
  // it fires grass_id_upload_cta_clicked ahead of any upload. The ref is a
  // per-hook cache on top of that shared sessionStorage entry.
  const ensureFunnelId = useCallback((): string => {
    if (funnelIdRef.current) return funnelIdRef.current
    return (funnelIdRef.current = ensureAnalysisFunnelId())
  }, [])

  // Dedupe `grass_id_zip_valid_entered` per attempt + ZIP value. Re-uploading
  // with the same ZIP should still attach ZIP context to the new attempt, but
  // clearing/retyping within one attempt should not double-fire.
  const seenValidZipsRef = useRef<Set<string>>(new Set())
  const fireZipValidEntered = useCallback((
    value: string,
    entryMethod: 'typed' | 'saved' | 'geolocation' | 'url_restore' | 'ip',
    mode: AnalysisMode,
  ) => {
    const attemptId = attemptIdRef.current ?? 'no-attempt'
    const dedupeKey = `${attemptId}:${value}`
    if (seenValidZipsRef.current.has(dedupeKey)) return
    seenValidZipsRef.current.add(dedupeKey)
    analytics.grassIdZipValidEntered({
      zipPrefix: value.slice(0, 3),
      entryMethod,
      mode,
      attemptId: attemptIdRef.current ?? undefined,
      funnelId: ensureFunnelId(),
      authState: user ? 'authenticated' : 'anonymous',
    })
  }, [ensureFunnelId, user])

  // Email gate state
  const [carePlanEmail, setCarePlanEmail] = useState("")
  const [emailSuggestion, setEmailSuggestion] = useState<EmailSuggestion | null>(null)
  const [isSubmittingCarePlan, setIsSubmittingCarePlan] = useState(false)
  const [emailGateCompleted, setEmailGateCompleted] = useState(false)
  const [carePlanEmailError, setCarePlanEmailError] = useState("")
  // Resolved timeline URL captured when the gate completes, so the on-page
  // success card's primary button can navigate immediately without rebuilding
  // it. A pending auto-advance timer is also tracked here so the "See my plan"
  // button can pre-empt the beat without double-navigating.
  const [completedTimelineUrl, setCompletedTimelineUrl] = useState<string | null>(null)
  const carePlanSubmitInFlightRef = useRef<boolean>(false)
  const carePlanRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const carePlanNavigatedRef = useRef<boolean>(false)

  // UI state

  // Set when the user comes back to the homepage after clicking the
  // "Read our free guide" link on a results page. Triggers a welcome-back
  // banner that re-presents the save CTA.
  const [returnedFromGuide, setReturnedFromGuide] = useState<{ guideSlug: string } | null>(null)

  // Only trust analysis images from the expected public storage path. Shared
  // with the server (analyze-grass + save-analysis routes) via
  // lib/image-url-validation so client and server can't drift — see that file
  // for the SSRF rationale.
  const isTrustedAnalysisImageUrl = useCallback((value: string): boolean =>
    isTrustedAnalysisImageUrlShared(value, {
      devFallbackHostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
    }), [])

  // Restore from URL params after OAuth redirect (works even without localStorage)
  useEffect(() => {
    const cleanUrlParams = (params: string[]) => {
      const url = new URL(window.location.href)
      let changed = false
      params.forEach((param) => {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param)
          changed = true
        }
      })
      if (changed) {
        window.history.replaceState(window.history.state, '', url.toString())
      }
    }

    // Consume the rescan capability into rescanOfAnalysisIdRef, then remove it
    // from the address bar immediately. This path also covers /?rescanOfAnalysisId=...
    // from a saved result, which intentionally has no OAuth resume image params.
    // Scrubbing the URL must not clear the ref: failed attempts may safely retry,
    // and the ref is cleared only after a successful result commit below.
    // `resumeAnalysis` / `analysisResumeId` belong here too. Both are read into
    // state at init, so leaving them in the address bar buys nothing and a
    // reload re-initialises the whole quota resume and runs it a second time.
    const earlyExitParams = [
      'rescanOfAnalysisId',
      'sourceResultAccessToken',
      'refinementTrigger',
      'refinementPrevOutcome',
      'refinementPrevConfidence',
      'resumeAnalysis',
      'analysisResumeId',
    ]
    if (!resumeFromUrl || !urlImageUrl) {
      if (urlRescanOfAnalysisId !== null || resumeAnalysisAfterAuth || urlAnalysisResumeId !== null) {
        cleanUrlParams(earlyExitParams)
      }
      return
    }
    // Only restore if we don't already have an uploaded image
    if (uploadedImageUrl) {
      if (urlRescanOfAnalysisId !== null || resumeAnalysisAfterAuth || urlAnalysisResumeId !== null) {
        cleanUrlParams(earlyExitParams)
      }
      return
    }

    if (!isTrustedAnalysisImageUrl(urlImageUrl)) {
      cleanUrlParams([
        'resume',
        'imageUrl',
        'supportingImageUrl',
        'zipCode',
        'rescanOfAnalysisId',
        'sourceResultAccessToken',
        'refinementTrigger',
        'refinementPrevOutcome',
        'refinementPrevConfidence',
        'resumeAnalysis',
        'analysisResumeId',
      ])
      trackEvent('analysis_url_restore_rejected', { mode: initialMode })
      return
    }

    urlRestoredPhotoRef.current = true
    setSkipZipAssignment(readSkipZipAssignment() ?? isSkipZipTreatment(skipZipVariant))
    // This document is the OAuth resume destination; the stored copy of this
    // path has done its job and must not replace the page again.
    clearPendingOAuthRedirect()
    setUploadedImageUrl(urlImageUrl)
    setUploadedImage(urlImageUrl)
    setSupportingImages(
      urlSupportingImageUrls
        .filter((url) => url !== urlImageUrl && isTrustedAnalysisImageUrl(url))
        .slice(0, 2)
        .map((url) => ({ url, preview: url })),
    )
    if (urlZipCode && validateZipCode(urlZipCode)) {
      setIsZipCodeValid(true)
      fireZipValidEntered(urlZipCode, 'url_restore', initialMode)
    }

    // A refinement that crossed OAuth lands back here. Put the user back in the
    // refinement step rather than a bare ZIP form, so the run they signed in for
    // is the one they finish. Lineage (rescanOfAnalysisIdRef + the access token)
    // is what keeps the promised free re-run off the monthly allowance; the
    // trigger is what makes the landing a refinement at all. An anonymous result the server never persisted has no root id
    // and no token, and gating the step on them dropped that visitor onto a
    // bare ZIP form after they signed in specifically to add a photo.
    const hasRefinementLineage = Boolean(rescanOfAnalysisIdRef.current && urlSourceResultAccessToken)
    const enteredRefinementStep = hasRefinementLineage || Boolean(urlRefinementTrigger)
    if (!enteredRefinementStep && !resumeAnalysisAfterAuth) {
      autoStartPhotoRef.current = urlImageUrl
    }
    if (enteredRefinementStep) {
      refinementTriggerRef.current = urlRefinementTrigger
      if (urlRefinementPreviousOutcome) {
        previousOutcomeRef.current = {
          outcome: urlRefinementPreviousOutcome,
          confidence: urlRefinementPreviousConfidence,
        }
      }
      setIsRefiningWithSupportingPhoto(true)
      setSupportingPhotoGuidance(
        urlRefinementTrigger === 'conflicting_rescan'
          ? REFINEMENT_GUIDANCE_CONFLICT[initialMode] ?? REFINEMENT_GUIDANCE_CONFLICT.identify
          : REFINEMENT_GUIDANCE_FALLBACK[initialMode],
      )
    }

    // Clean up the URL params so they don't persist on refresh
    cleanUrlParams([
      'resume',
      'imageUrl',
      'supportingImageUrl',
      'zipCode',
      'rescanOfAnalysisId',
      'sourceResultAccessToken',
      'refinementTrigger',
      'refinementPrevOutcome',
      'refinementPrevConfidence',
      'resumeAnalysis',
      'analysisResumeId',
    ])

    trackEvent('analysis_url_restored', { mode: initialMode })

    // analysis_url_restored fires identically whether the visitor landed in the
    // refinement step or on a bare ZIP form, so the loss the gate funnel keeps
    // reporting was invisible at the landing itself. Emit the shape of the
    // landing separately, only for returns that claim to be refinements.
    if (enteredRefinementStep || urlRefinementPreviousOutcome) {
      analytics.refinementResumeLanded({
        mode: initialMode,
        hasLineage: hasRefinementLineage,
        hasPrevOutcome: Boolean(urlRefinementPreviousOutcome),
        enteredRefinementStep,
        trigger: urlRefinementTrigger,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pairs with the guide-link onClick in results-display.tsx — restoring on
  // mount turns a guide-page bounce into a returning visitor with the save
  // CTA back in front of them.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('returnedFromGuide_pending')
      if (!raw) return
      const parsed = JSON.parse(raw) as {
        analysisResult?: AnalysisResult
        uploadedImageUrl?: string
        zipCode?: string
        savedAnalysisId?: string | null
        guideSlug?: string
        savedAt?: number
      }

      const ONE_HOUR = 60 * 60 * 1000
      if (!parsed.savedAt || Date.now() - parsed.savedAt > ONE_HOUR) {
        localStorage.removeItem('returnedFromGuide_pending')
        return
      }

      // Don't clobber an in-flight upload (e.g. another tab mid-analysis)
      if (uploadedImageUrl || analysisResult) return

      if (!parsed.analysisResult || !parsed.uploadedImageUrl) {
        localStorage.removeItem('returnedFromGuide_pending')
        return
      }
      if (!isTrustedAnalysisImageUrl(parsed.uploadedImageUrl)) {
        localStorage.removeItem('returnedFromGuide_pending')
        return
      }

      setAnalysisResult(parsed.analysisResult)
      setUploadedImage(parsed.uploadedImageUrl)
      setUploadedImageUrl(parsed.uploadedImageUrl)
      if (parsed.zipCode) {
        setZipCode(parsed.zipCode)
        if (validateZipCode(parsed.zipCode)) {
          setIsZipCodeValid(true)
          fireZipValidEntered(parsed.zipCode, 'saved', analysisMode)
        }
      }
      if (parsed.savedAnalysisId) setSavedAnalysisId(parsed.savedAnalysisId)
      if (parsed.guideSlug) setReturnedFromGuide({ guideSlug: parsed.guideSlug })

      // Seed a `restored:`-prefixed attempt id so the visibility observer
      // actually fires grass_id_result_viewed for this rehydrated result
      // (it exits early on a null attempt id). The fresh-analysis API path
      // never ran here, so grass_id_result_rendered won't fire — the
      // `restored` prefix is what keeps this view out of the bounce-before-
      // scroll delta. Mirrors the popstate `/result/<id>` restore branch.
      // Anonymous unsaved returns have no analysis id, so fall back to a
      // stable sentinel that still satisfies the `restored:` prefix check.
      attemptIdRef.current = parsed.savedAnalysisId
        ? `restored:${parsed.savedAnalysisId}`
        : 'restored:guide'

      localStorage.removeItem('returnedFromGuide_pending')
      trackEvent('results_restored_after_guide', {
        grass_type: parsed.analysisResult.grassType,
        guide_slug: parsed.guideSlug,
      })
    } catch {
      /* localStorage unavailable or bad JSON */
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-persist upload state so it survives OAuth redirects and page navigation.
  // Saved whenever there's an uploaded image; cleared on reset or successful analysis.
  useEffect(() => {
    if (!uploadedImageUrl || isAnalyzing || analysisResult || diagnosisResult) return
    // Recovery already consumed its handoff. A fresh-auth refresh must not
    // interpret this retained error photo as a request to start another scan.
    if (recoveryRestoredRef.current && (analysisNetworkError || analysisSaveError || analysisServerError)) return
    if (!isTrustedAnalysisImageUrl(uploadedImageUrl)) return
    try {
      localStorage.setItem('pendingAnalysis_flow', JSON.stringify({
        imageUrl: uploadedImageUrl,
        imageUrls: [uploadedImageUrl, ...supportingImages.map((image) => image.url)],
        zipCode,
        analysisMode,
        savedAt: Date.now(),
      }))
    } catch { /* localStorage unavailable */ }
  }, [uploadedImageUrl, supportingImages, zipCode, analysisMode, isTrustedAnalysisImageUrl, isAnalyzing, analysisResult, diagnosisResult, analysisNetworkError, analysisSaveError, analysisServerError])

  // Restore analysis state after OAuth signup or page navigation.
  // Only fires when user auth state changes (null → authenticated after OAuth).
  useEffect(() => {
    if (!user) return
    if (recoveryReturnId) return

    // `_flow` is auto-written on every upload as a redundant backup. Restoring
    // it on every signed-in homepage visit would stick the user on the ZIP
    // step after a plain refresh. Only restore it when AuthContext has just
    // observed a fresh SIGNED_IN event (the `justAuthed` sentinel) — that
    // catches the edge case of "uploaded → left to sign in elsewhere → came
    // back" without firing on plain refreshes. The sentinel window is 180s.
    //
    // We don't drop the sentinel on read — instead a parallel
    // `justAuthedConsumed` flag marks whether a previous mount already
    // rehydrated `_flow`. That way a second mount inside the window can still
    // restore once if the first mount didn't have stored state to restore.
    const allowFlowRestore = wasJustAuthed()

    // A URL restore (resume=1&imageUrl=...) is the richer source: it carries
    // the supporting photos and the refinement lineage. The URL effect above
    // has already queued that state, but this closure still sees the pre-mount
    // `uploadedImageUrl`, so the `_flow` duplicate below would overwrite the
    // supporting set with whatever was last auto-persisted. Leave `_flow` out
    // whenever the URL restore actually applied a photo; a rejected (untrusted)
    // URL leaves the ref false so `_flow` still stands in.
    const urlCarriesPhoto = urlRestoredPhotoRef.current
    const keys: readonly ('pendingAnalysis_limit' | 'pendingAnalysis_diagnosis' | 'pendingAnalysis_flow')[] =
      allowFlowRestore && !urlCarriesPhoto
        ? ['pendingAnalysis_limit', 'pendingAnalysis_diagnosis', 'pendingAnalysis_flow']
        : ['pendingAnalysis_limit', 'pendingAnalysis_diagnosis']
    let restoredQuotaResume = false

    // Did this mount follow a sign-in? The branches below drop a dead
    // `pendingAnalysis_limit` on EVERY signed-in mount, so without this gate a
    // week-old stale key would report a broken promise on an unrelated visit.
    // Only a post-auth return can falsify "sign in and this photo runs".
    const returningFromAuth = allowFlowRestore || resumeAnalysisAfterAuth

    /**
     * The limit wall promises the photo runs on return. Every `continue` below
     * is a case where that promise silently died: the payload was written, the
     * user signed in, and they landed on an empty uploader with no error and no
     * event. Measured over 90 days, 7 people signed in without a resume and only
     * 1 produced a diagnostic, so 6 of 7 failures were invisible.
     *
     * Reported on the SAME event as the existing failure path so the whole
     * funnel joins on one name and splits on `reason`.
     */
    let reportedRestoreFailure = false
    const reportResumeRestoreFailure = (
      reason: string,
      extra?: Record<string, unknown>,
    ) => {
      if (!returningFromAuth || reportedRestoreFailure) return
      reportedRestoreFailure = true
      trackEvent('analysis_resume_restore_failed', {
        reason,
        entry_path: pathname,
        restore_source: 'local_storage',
        ...extra,
      })
    }

    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key)
        if (!raw) continue
        const pending = JSON.parse(raw) as {
          imageUrl?: string
          imageUrls?: string[]
          zipCode?: string
          analysisMode?: AnalysisMode
          analysisId?: string
          resultAccessToken?: string
          planType?: 'treatment' | 'monitoring'
          outcome?: string
          entryPath?: string
          subject?: string
          savedAt?: number
          resumeAnalysis?: boolean
          analysisResumeId?: string
        }

        // Expire before attempting any ownership claim. The image URL and
        // result token are both short-lived handoff credentials.
        const ONE_HOUR = 60 * 60 * 1000
        if (!pending.savedAt || Date.now() - pending.savedAt > ONE_HOUR) {
          // Expected to dominate the silent failures: the wall's handoff lives
          // one hour, and a visitor who parks the OAuth tab and finishes later
          // returns to a dead payload. `age_minutes` is what says whether the
          // window is simply too short.
          if (key === 'pendingAnalysis_limit' && pending.resumeAnalysis === true) {
            reportResumeRestoreFailure('expired', {
              analysis_resume_id: pending.analysisResumeId,
              mode: pending.analysisMode,
              age_minutes: pending.savedAt
                ? Math.round((Date.now() - pending.savedAt) / 60000)
                : null,
            })
          }
          localStorage.removeItem(key)
          continue
        }

        // Claim the anon diagnosis row onto the just-signed-in account so it
        // lands in /dashboard/history (and stays retrievable beyond the anon
        // share URL). Fire-and-forget: the rehydrate below is the fallback if
        // this fails, and a failed claim must never block restore. Runs before
        // the `uploadedImageUrl` early-break so it fires even when the page
        // already has an image in state.
        if (key === 'pendingAnalysis_diagnosis' && user && pending.analysisId) {
          claimAnalysisOnce(pending.analysisId, 'diagnosis_oauth_return', {
            resultAccessToken: pending.resultAccessToken,
            analyticsContext: {
              analysisId: pending.analysisId,
              mode: 'diagnose',
              planType: pending.planType || 'treatment',
              outcome: pending.outcome || 'problems',
              entryPath: pending.entryPath || 'diagnosis_oauth_return',
              subject: pending.subject,
            },
          })
        }

        // _limit and _diagnosis are one-shot restore keys. _flow is a
        // duplicate of whichever gate-trigger fired (it's auto-written on
        // every upload), so once we restore from a gate key we also drop
        // _flow to prevent a stale image rehydrating in a later session.
        if (key !== 'pendingAnalysis_flow') {
          localStorage.removeItem(key)
          localStorage.removeItem('pendingAnalysis_flow')
        }

        // Skip if we already have an uploaded image (don't overwrite current state)
        if (uploadedImageUrl) break

        if (!pending.imageUrl || !isTrustedAnalysisImageUrl(pending.imageUrl)) {
          // The trust check is what makes "no re-upload" structurally true: a
          // local blob URL can never satisfy it. If this ever fires in volume,
          // the photo is not reaching storage before the wall renders, which
          // breaks the promise at its root rather than at the handoff.
          if (key === 'pendingAnalysis_limit' && pending.resumeAnalysis === true) {
            reportResumeRestoreFailure('untrusted_image_url', {
              analysis_resume_id: pending.analysisResumeId,
              mode: pending.analysisMode,
              had_image_url: Boolean(pending.imageUrl),
            })
          }
          localStorage.removeItem(key)
          continue
        }
        setSkipZipAssignment(readSkipZipAssignment() ?? isSkipZipTreatment(skipZipVariant))
        if (pending.resumeAnalysis !== true && !resumeAnalysisAfterAuth) {
          autoStartPhotoRef.current = pending.imageUrl
        }
        setUploadedImageUrl(pending.imageUrl)
        setUploadedImage(pending.imageUrl)
        if (Array.isArray(pending.imageUrls)) {
          setSupportingImages(
            pending.imageUrls
              .filter((url) => url !== pending.imageUrl && isTrustedAnalysisImageUrl(url))
              .slice(0, 2)
              .map((url) => ({ url, preview: url })),
          )
        }
        if (pending.zipCode) {
          setZipCode(pending.zipCode)
          if (validateZipCode(pending.zipCode)) {
            setIsZipCodeValid(true)
            fireZipValidEntered(pending.zipCode, 'saved', pending.analysisMode || analysisMode)
          }
        }
        if (pending.analysisMode) {
          setAnalysisMode(pending.analysisMode)
        }
        if (pending.resultAccessToken) {
          setResultAccessToken(pending.resultAccessToken)
        }

        if (
          key === 'pendingAnalysis_limit' &&
          pending.resumeAnalysis === true &&
          typeof pending.analysisResumeId === 'string' &&
          ANALYSIS_RESUME_ID_RE.test(pending.analysisResumeId)
        ) {
          const resumeMode = pending.analysisMode || analysisMode
          const entryPath = pending.entryPath === '/diagnose' ? '/diagnose' : '/'
          analysisResumeFlowRef.current = {
            id: pending.analysisResumeId,
            mode: resumeMode,
            entryPath,
          }
          restoredQuotaResume = true
          setQuotaResumeId(pending.analysisResumeId)
          setQuotaResumePending(true)
          trackEvent('analysis_resume_restored', {
            analysis_resume_id: pending.analysisResumeId,
            mode: resumeMode,
            entry_path: entryPath,
            restore_source: 'local_storage',
          })
          try {
            const cleanUrl = new URL(window.location.href)
            cleanUrl.searchParams.delete('resumeAnalysis')
            cleanUrl.searchParams.delete('analysisResumeId')
            window.history.replaceState(window.history.state, '', cleanUrl.toString())
          } catch { /* URL cleanup is best-effort */ }
        }

        trackEvent(
          key === 'pendingAnalysis_limit' ? 'analysis_limit_restored'
            : key === 'pendingAnalysis_diagnosis' ? 'analysis_diagnosis_restored'
            : 'analysis_flow_restored',
          // `unlock` separates this OAuth-return restore from the in-place
          // email unlock, which fires the same event name.
          { mode: pending.analysisMode, unlock: 'oauth' }
        )

        // Mark the justAuthed sentinel as consumed once we've successfully
        // rehydrated. A subsequent mount in the same 180s window won't try
        // to restore again. Clear both keys for cleanliness.
        if (key === 'pendingAnalysis_flow') {
          try {
            window.sessionStorage.setItem('justAuthedConsumed', '1')
            window.sessionStorage.removeItem('justAuthed')
            window.sessionStorage.removeItem('justAuthedConsumed')
          } catch { /* sessionStorage unavailable */ }
        }
        break // Only restore the first valid pending analysis
      } catch {
        // localStorage unavailable (private mode, disabled storage) or bad
        // JSON. Silent until now, and indistinguishable from "never wrote
        // anything" in the funnel.
        if (key === 'pendingAnalysis_limit') {
          try { reportResumeRestoreFailure('unreadable_payload') } catch { /* never mask the original fault */ }
        }
      }
    }
    if (
      resumeAnalysisAfterAuth &&
      !restoredQuotaResume &&
      !uploadedImageUrl &&
      !urlImageUrl
    ) {
      setUploadError('You are signed in, but this browser could not restore the uploaded photo. Add it again to continue your analysis here.')
      // The message always shows — the user still lost their photo either way.
      // The EVENT is the generic fallback: if a branch above already named the
      // specific cause, reporting again here would count one failure twice.
      if (!reportedRestoreFailure) {
        trackEvent('analysis_resume_restore_failed', {
          analysis_resume_id: urlAnalysisResumeId || undefined,
          mode: initialMode,
          entry_path: pathname,
          reason: 'missing_browser_state',
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Recovery sign-in restores the screen without starting another analysis.
  useEffect(() => {
    if (!user || !recoveryReturnId || recoveryRestoredRef.current || isOAuthRedirectReplacing()) return
    recoveryRestoredRef.current = true
    clearPendingOAuthRedirect()
    const pending = readScanRecovery(recoveryReturnId, isTrustedAnalysisImageUrl)
    if (!pending) {
      setUploadError('You are signed in, but this browser could not restore your photo. Add it again to continue.')
      trackEvent('scan_recovery_restore_failed', { mode: initialMode })
      return
    }
    autoStartPhotoRef.current = null
    setSkipZipAssignment(pending.skipZip)
    saveSkipZipAssignment(pending.skipZip)
    setUploadedImageUrl(pending.imageUrl)
    setUploadedImage(pending.imageUrl)
    setSupportingImages(pending.supportingImageUrls.slice(0, 2).map((url) => ({ url, preview: url })))
    setZipCode(pending.zipCode)
    setIsZipCodeValid(validateZipCode(pending.zipCode))
    setAnalysisMode(pending.mode)
    setAnalysisResult(pending.reason === 'photo' ? pending.result : null)
    setSavedAnalysisId(pending.analysisId)
    setResultAccessToken(pending.resultAccessToken)
    funnelIdRef.current = pending.funnelId
    attemptIdRef.current = pending.attemptId
    setAnalysisNetworkError(pending.reason === 'network')
    setAnalysisSaveError(pending.reason === 'save')
    setAnalysisServerError(pending.reason === 'server')
    if (pending.refinement) {
      rescanOfAnalysisIdRef.current = pending.refinement.rootId
      refinementTriggerRef.current = pending.refinement.trigger
      setIsRefiningWithSupportingPhoto(true)
      setSupportingPhotoGuidance(pending.refinement.guidance)
    }
    try {
      localStorage.removeItem(SCAN_RECOVERY_KEY)
      localStorage.removeItem('pendingAnalysis_flow')
      const cleanUrl = new URL(window.location.href)
      cleanUrl.searchParams.delete('scanRecovery')
      window.history.replaceState(window.history.state, '', cleanUrl.toString())
    } catch { /* The restored screen remains usable if storage is unavailable. */ }
    trackEvent('scan_recovery_restored', { mode: pending.mode, reason: pending.reason, funnel_id: pending.funnelId })
  }, [user, recoveryReturnId, isTrustedAnalysisImageUrl, initialMode])

  // WHERE the current upload's file was picked (blog inline-scan handoff vs the
  // homepage uploader). Set by the pending-scan-file pickup effect below, cleared
  // once stamped onto grass_id_photo_uploaded, so a later manual re-upload in the
  // same session correctly reads 'homepage'.
  const entrySourceRef = useRef<string | null>(null)

  // Image upload handler — accepts a File directly so it can be wired to either
  // a native <input> change event or react-dropzone's onDrop. `source`
  // distinguishes the capture path (camera vs picker vs drag-drop) so the
  // funnel can compare them; omitted for programmatic callers.
  const handleImageUpload = useCallback(async (file: File, source?: 'file' | 'drop' | 'camera') => {
    if (!file) return
    ensureFunnelId()

    setUploadError("")

    // Pre-checks for programmatic callers; the dropzone enforces these too.
    // iOS sometimes drops a HEIC file with an empty `type`; fall back to the
    // file extension so we still accept it and let the converter handle it.
    const allowedTypes = Object.keys(UPLOAD_ACCEPT)
    const lowerName = file.name.toLowerCase()
    const matchesByExtension = Object.values(UPLOAD_ACCEPT)
      .flat()
      .some((ext) => lowerName.endsWith(ext))
    const precheckReason: UploadPrecheckReason | null =
      !allowedTypes.includes(file.type) && !matchesByExtension
        ? 'invalid_file_type'
        : file.size > UPLOAD_MAX_BYTES_HOMEPAGE
          ? 'file_too_large'
          : null
    if (precheckReason) {
      setUploadError(UPLOAD_REJECTION_COPY[precheckReason])
      trackUploadPrecheckRejected({
        reason: precheckReason,
        capturePath: source ?? 'unknown',
        mode: analysisMode,
        funnelId: funnelIdRef.current,
        fileSizeBytes: file.size,
      })
      return
    }

    // A new primary photo starts a new analysis root — a pending supporting-
    // photo refinement (and any views collected for the old root) no longer
    // applies to it. resetUpload covers the normal path; this covers direct
    // programmatic uploads (blog handoff, pending-scan consume).
    setIsRefiningWithSupportingPhoto(false)
    setSupportingPhotoGuidance(null)
    setSupportingImages([])
    setSupportingImageError("")
    setPhotoQuality(null)
    setSupportingPhotoQuality({})
    photoQualityTokenRef.current = null
    supportingQualityUrlsRef.current.clear()

    setIsUploading(true)
    // Freeze the branch before any uploaded-photo UI can mount. A late flag
    // response must never turn a visible ZIP form into an automatic scan.
    const skipZip = skipZipAssignment ?? isSkipZipTreatment(skipZipVariant)
    setSkipZipAssignment(skipZip)
    if (skipZipVariant !== undefined) saveSkipZipAssignment(skipZip)
    if (skipZip) {
      const savedZip = zipCode || readSavedZip() || ''
      setZipCode(savedZip)
      setIsZipCodeValid(/^\d{5}$/.test(savedZip) && savedZip !== '00000')
    }
    if (!skipZip && zipIpPrefillVariantRef.current === true) void prefetchIpZip()
    const pendingRetry = pendingRetryRef.current
    const hadResult = !!(analysisResult || diagnosisResult)
    const previousAttemptId = attemptIdRef.current
    const attemptId = newAttemptId()
    let uploadFailureContext: Record<string, string | number | boolean | undefined> = {
      attempt_id: attemptId,
    }

    // Start-of-upload marker — grass_id_photo_uploaded only fires after the
    // storage roundtrip succeeds, so without this the compression/upload
    // abandon window (slow mobile connections backgrounding the tab) was
    // invisible in the funnel.
    trackEvent('grass_id_upload_started', {
      ...analysisFunnelEnvelope({
        funnelId: funnelIdRef.current,
        attemptId,
        originLawnId: options?.lawnContext?.lawnId ?? null,
        selectedLawnId: options?.lawnContext?.lawnId ?? null,
        mode: analysisMode,
        source: source ?? 'unknown',
        imageRoles: ['primary'],
        authState: user ? 'authenticated' : 'anonymous',
        actualDestination: 'image_upload',
      }),
      attempt_id: attemptId,
      source: source ?? 'unknown',
      file_size_kb: Math.round(file.size / 1024),
      mode: analysisMode,
    })

    try {
      // Preserve accepted originals whenever they already fit the active size
      // guard. The accuracy beta raises the bounded ceiling; it does not bypass
      // the upload limit or send unlimited camera originals.
      const preparedImage = await prepareGrassImage(file, 'primary')
      const compressionResult = preparedImage.result

      const fileToUpload = compressionResult.file

      // Pre-call capture check. Fire-and-forget on purpose: it must never delay
      // the upload fetch below. It measures the already-compressed file, so the
      // second decode is on a bounded image rather than a 12MP camera original,
      // and measurePhotoQuality never rejects, so a missing canvas (SSR, jsdom,
      // thumbnail capture) simply yields no warning.
      photoQualityTokenRef.current = attemptId
      const qualityStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
      void measurePhotoQuality(fileToUpload)
        .then((verdict) => {
          if (photoQualityTokenRef.current !== attemptId) return
          if (!verdict) return
          const finishedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
          const measureMs = finishedAt - qualityStartedAt

          // Every measurement, pass or warn. The warning events only describe
          // the flagged tail, so on their own they can find a threshold too
          // loose but never too tight, and the false negatives stay invisible.
          // This carries the same attempt_id, so the pass distribution joins to
          // grass_identification_outcome.
          trackEvent('capture_quality_measured', {
            ...analysisFunnelEnvelope({
              funnelId: funnelIdRef.current,
              attemptId,
              mode: analysisMode,
              source: source ?? 'unknown',
              imageRoles: ['primary'],
              authState: user ? 'authenticated' : 'anonymous',
              actualDestination: 'zip_step',
            }),
            attempt_id: attemptId,
            ...photoQualityMeasurementProps({ verdict, role: 'primary', measureMs }),
          })

          if (verdict.ok || !verdict.reason) return
          setPhotoQuality({
            reason: verdict.reason,
            metrics: verdict.metrics,
            role: 'primary',
            attemptId,
            measureMs,
          })
        })
        .catch(() => { /* fail-open by contract */ })

      // Log compression if it occurred
      if (compressionResult.wasCompressed) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            `Image compressed: ${formatFileSize(compressionResult.originalSize)} → ${formatFileSize(compressionResult.compressedSize)}`
          )
        }
      }

      // Create FormData for upload
      const formData = new FormData()
      formData.append('file', fileToUpload)
      formData.append('category', 'grass-analysis')
      formData.append('attemptId', attemptId)

      // Upload to Supabase storage
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        body: formData
      })

      // Parse defensively: a framework-level 500 (function timeout, sharp OOM
      // on a large mobile photo) returns a plain-text body, and response.json()
      // would throw a SyntaxError that masks the real status. See lib/safe-json.
      const result = (await safeJson<{
        success?: boolean
        url?: string
        error?: unknown
        code?: unknown
        imageMetadata?: CanonicalUploadMetadata | null
      }>(response)) ?? {}

      if (!response.ok) {
        uploadFailureContext = {
          attempt_id: attemptId,
          http_status: response.status,
          server_error_code: typeof result.code === 'string' ? result.code : undefined,
          server_error: errorMessageFrom(result.error, `Upload failed (${response.status})`).slice(0, 200),
        }
        if (response.status === 429) {
          const rateLimitMsg = "You've uploaded a lot of photos in the last hour. Please wait a few minutes and try again."
          setUploadError(rateLimitMsg)
          setIsUploading(false)
          toast.error('Upload Rate Limit', { description: rateLimitMsg, position: 'top-right', duration: 5000, closeButton: true })
          // Terminal event for this attempt — without it a rate-limited upload
          // is indistinguishable from abandonment after upload_started.
          trackEvent('grass_id_upload_rejected', {
            ...analysisFunnelEnvelope({
              funnelId: funnelIdRef.current,
              attemptId,
              mode: analysisMode,
              source: source ?? 'unknown',
              imageRoles: ['primary'],
              authState: user ? 'authenticated' : 'anonymous',
              actualDestination: 'upload_error',
            }),
            reason: 'rate_limited',
            attempt_id: attemptId,
            source: source ?? 'unknown',
            mode: analysisMode,
          })
          return
        }
        throw new Error(errorMessageFrom(result.error, `Upload failed (${response.status})`))
      }

      if (result.success) {
        // This upload's attempt id was already sent to the server. If a result was
        // visible, this is a retry — fire retry_started before swapping ids so
        // the previous attempt id can be referenced.
        if (pendingRetry) {
          analytics.grassIdRetryStarted({
            ...pendingRetry,
            mode: analysisMode,
            funnelId: funnelIdRef.current ?? undefined,
            selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
          })
          pendingRetryRef.current = null
        }
        if (!pendingRetry && hadResult) {
          analytics.grassIdRetryStarted({
            previousAttemptId,
            fromResult: true,
            mode: analysisMode,
            funnelId: funnelIdRef.current ?? undefined,
            selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
          })
        } else if (!pendingRetry && previousAttemptId) {
          // No result visible but a prior attempt existed (failed/abandoned).
          analytics.grassIdRetryStarted({
            previousAttemptId,
            fromResult: false,
            mode: analysisMode,
            funnelId: funnelIdRef.current ?? undefined,
            selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
          })
        }
        attemptIdRef.current = attemptId
        resultViewedFiredForRef.current = null
        // A fresh photo starts clean — drop any leftover retry state.
        setAnalysisNetworkError(false)
        setAnalysisSaveError(false)
        setAnalysisServerError(false)

        // Store the uploaded URL for display and database
        if (skipZip && result.url) autoStartPhotoRef.current = result.url
        setUploadedImageUrl(result.url ?? null)
        // Store the compressed file for potential future use
        setUploadedImageFile(fileToUpload)
        // Also store a local preview for immediate display
        const reader = new FileReader()
        reader.onload = (e) => {
          setUploadedImage(e.target?.result as string)
        }
        reader.readAsDataURL(fileToUpload)
        setIsUploading(false)

        // Track photo upload with compression info
        const canonicalMetadata = result.imageMetadata ?? null
        const canonicalSizeKb = canonicalMetadata
          ? Math.round(canonicalMetadata.outputBytes / 1024)
          : Math.round(fileToUpload.size / 1024)
        const canonicalWidth = canonicalMetadata?.outputWidth ?? compressionResult.finalDimensions?.width
        const canonicalHeight = canonicalMetadata?.outputHeight ?? compressionResult.finalDimensions?.height
        analytics.grassIdPhotoUploaded(
          canonicalSizeKb,
          compressionResult.wasCompressed ? 'file-compressed' : 'file',
          {
            attemptId,
            source,
            // Blog handoff source when set; otherwise report the ACTUAL surface
            // (this hook also runs on /result/[id] and /diagnose — a bare
            // 'homepage' default would misattribute those uploads). Slugged
            // through the shared mapper so it joins `route_surface`.
            entrySource: entrySourceRef.current ?? surfaceFromPathname(pathname),
            mode: analysisMode,
            funnelId: funnelIdRef.current ?? undefined,
            originLawnId: options?.lawnContext?.lawnId ?? null,
            selectedLawnId: options?.lawnContext?.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
            originalSizeKb: Math.round(compressionResult.originalSize / 1024),
            width: canonicalWidth,
            height: canonicalHeight,
            highFidelity: HIGH_FIDELITY_IMAGE_UPLOADS,
            imageRole: 'primary',
            fidelityFallbackReason: preparedImage.fidelityFallbackReason,
            clientPreparedSizeKb: Math.round(fileToUpload.size / 1024),
            serverQuality: canonicalMetadata?.serverQuality,
            serverResized: canonicalMetadata?.wasResized,
          },
        )
        // Consume-once: a later manual re-upload in this session is homepage-
        // originated and must not inherit the blog handoff attribution.
        entrySourceRef.current = null
        primaryImageMetricsRef.current = {
          originalSizeKb: Math.round(compressionResult.originalSize / 1024),
          uploadedSizeKb: canonicalSizeKb,
          width: canonicalWidth,
          height: canonicalHeight,
        }
      } else {
        uploadFailureContext = {
          attempt_id: attemptId,
          http_status: response.status,
          server_error_code: typeof result.code === 'string' ? result.code : undefined,
          server_error: errorMessageFrom(result.error, 'Upload failed').slice(0, 200),
        }
        throw new Error(errorMessageFrom(result.error, 'Upload failed'))
      }
    } catch (error) {
      console.error('Upload error:', error)
      setIsUploading(false)

      // Provide specific error messages for compression errors
      let errorMsg = "Failed to upload image. Please try again."

      if (isTransientNetworkError(error)) {
        // iOS Safari aborts the in-flight multipart upload when the tab is
        // backgrounded mid-request (this is the slowest call in the flow). The
        // request simply never completed — the photo and server are fine — so
        // surface a recoverable retry message and DON'T pollute Error Tracking
        // with what is an expected mobile-connectivity event. Tagged distinctly
        // so the funnel can separate connection drops from real upload bugs.
        errorMsg = "Your connection dropped before the photo finished uploading. Please try again."
        trackEvent('grass_id_upload_rejected', {
          ...analysisFunnelEnvelope({
            funnelId: funnelIdRef.current,
            attemptId,
            mode: analysisMode,
            source: source ?? 'unknown',
            imageRoles: ['primary'],
            authState: user ? 'authenticated' : 'anonymous',
            actualDestination: 'upload_error',
          }),
          reason: 'network_aborted',
          attempt_id: attemptId,
          source: source ?? 'unknown',
          mode: analysisMode,
        })
      } else if (error instanceof ImageCompressionError) {
        // Handled, user-fixable input problem (corrupt / unsupported file).
        // It's a product signal, not a bug — record it as an event instead of
        // polluting Error Tracking with handled exceptions.
        errorMsg = error.message
        trackEvent('grass_id_upload_rejected', {
          ...analysisFunnelEnvelope({
            funnelId: funnelIdRef.current,
            attemptId,
            mode: analysisMode,
            source: source ?? 'unknown',
            imageRoles: ['primary'],
            authState: user ? 'authenticated' : 'anonymous',
            actualDestination: 'upload_error',
          }),
          reason: error.code,
          attempt_id: attemptId,
          source: source ?? 'unknown',
          mode: analysisMode,
        })
      } else if (uploadFailureContext?.server_error_code === 'invalid_form_data') {
        // The server saw a malformed/truncated multipart body (usually an
        // upload cut off mid-flight). Recoverable by retrying — an event, not
        // an Error Tracking exception.
        errorMsg = 'The photo upload was interrupted. Please try again.'
        trackEvent('grass_id_upload_rejected', {
          ...analysisFunnelEnvelope({
            funnelId: funnelIdRef.current,
            attemptId,
            mode: analysisMode,
            source: source ?? 'unknown',
            imageRoles: ['primary'],
            authState: user ? 'authenticated' : 'anonymous',
            actualDestination: 'upload_error',
          }),
          reason: 'invalid_form_data',
          attempt_id: attemptId,
          source: source ?? 'unknown',
          mode: analysisMode,
        })
      } else if (isExpectedUploadRejection(uploadFailureContext)) {
        // Server-rejected input (corrupt/oversized/unsupported photo). The
        // route's body is already the user-facing sentence, so show it verbatim
        // and record a product event — never a second Error Tracking issue for
        // a failure the server has already telemetered.
        if (error instanceof Error && error.message) {
          errorMsg = error.message
        }
        trackEvent('grass_id_upload_rejected', {
          ...analysisFunnelEnvelope({
            funnelId: funnelIdRef.current,
            attemptId,
            mode: analysisMode,
            source: source ?? 'unknown',
            imageRoles: ['primary'],
            authState: user ? 'authenticated' : 'anonymous',
            actualDestination: 'upload_error',
          }),
          reason: typeof uploadFailureContext.server_error_code === 'string'
            ? uploadFailureContext.server_error_code
            : `http_${uploadFailureContext.http_status}`,
          http_status: uploadFailureContext.http_status,
          attempt_id: attemptId,
          source: source ?? 'unknown',
          mode: analysisMode,
        })
      } else {
        if (error instanceof Error && error.message) {
          errorMsg = error.message
        }
        // Unexpected failure — capture to Error Tracking for investigation.
        performanceAnalytics.captureError({
          error,
          source: 'api',
          severity: 'error',
          context: { error_type: 'upload_failed', stage: 'image_upload', ...uploadFailureContext },
        })
      }

      setUploadError(errorMsg)
    }
  }, [analysisResult, diagnosisResult, analysisMode, ensureFunnelId, newAttemptId, pathname, options?.lawnContext?.lawnId, user, skipZipAssignment, skipZipVariant, zipCode])

  // Blog inline-scan handoff pickup: a photo picked on a blog post (see
  // components/blog/inline-scan-cta.tsx) rides the client-side navigation in a
  // module stash. Consume it once on mount and start the normal upload path so
  // the reader lands here already analyzing. External-input effect, not derived
  // state. The stash is TTL-bounded and consume-once, so hard reloads and
  // repeat mounts are no-ops.
  //
  // Grass-ID + diagnose only: this hook also mounts on /result/[id]. The blog
  // handoff targets '/#analysis-card' (grass-ID) or '/diagnose' (diagnose
  // picker V2), so both of those routes consume the stash and start analyzing;
  // any OTHER surface must leave it alone — a back/forward remount of a result
  // page inside the TTL window must not swallow the photo (or worse, start an
  // upload there). No double-consume risk: the routes are separate and the
  // stash is module-level consume-once.
  useEffect(() => {
    if (pathname !== '/' && pathname !== '/diagnose') return
    const pending = consumePendingScanFile()
    if (!pending) return
    entrySourceRef.current = pending.source
    void handleImageUpload(pending.file, 'file')
  // Mount-only by design: the stash can only be non-empty on arrival.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Supporting photos are collected only in the post-result refinement lane;
  // ZipCodeStep exposes the slots when `isRefinementStep` is set. The API still
  // receives imageUrl for backward compatibility plus imageUrls when supporting
  // views exist.
  const handleSupportingImagesUpload = useCallback(async (files: File[]) => {
    const slots = Math.max(0, supportingPhotoSlots - supportingImages.length)
    const selected = files.slice(0, slots)
    if (selected.length === 0) return

    setSupportingImageError("")
    setIsUploadingSupportingImages(true)
    const attemptId = attemptIdRef.current ?? newAttemptId()
    if (!attemptIdRef.current) attemptIdRef.current = attemptId

    const uploaded: SupportingAnalysisImage[] = []
    try {
      for (const file of selected) {
        const allowedTypes = Object.keys(UPLOAD_ACCEPT)
        const lowerName = file.name.toLowerCase()
        const matchesByExtension = Object.values(UPLOAD_ACCEPT)
          .flat()
          .some((ext) => lowerName.endsWith(ext))
        if ((!allowedTypes.includes(file.type) && !matchesByExtension) || file.size > UPLOAD_MAX_BYTES_HOMEPAGE) {
          throw new Error("Supporting photos must be JPEG, PNG, WebP, or HEIC files within the upload limit.")
        }

        const preparedImage = await prepareGrassImage(file, 'supporting')
        const compressionResult = preparedImage.result
        const formData = new FormData()
        formData.append('file', compressionResult.file)
        formData.append('category', 'grass-analysis')
        formData.append('attemptId', attemptId)

        const response = await fetch('/api/upload/image', { method: 'POST', body: formData })
        const data = (await safeJson<{
          success?: boolean
          url?: string
          error?: unknown
          imageMetadata?: CanonicalUploadMetadata | null
        }>(response)) ?? {}
        if (!response.ok || !data.success || !data.url || !isTrustedAnalysisImageUrl(data.url)) {
          throw new Error(errorMessageFrom(data.error, 'A supporting photo could not be uploaded.'))
        }

        // Reuse the bounded uploaded URL for preview. Serializing base64 camera
        // data into history.state can exceed browser history limits with two
        // high-fidelity images and break back/forward restoration.
        uploaded.push({ url: data.url, preview: data.url })
        // Keyed by the uploaded URL, which is what the slot list and
        // removeSupportingImage already key on. Fire-and-forget, same contract
        // as the primary path.
        const supportingUrl = data.url
        supportingQualityUrlsRef.current.add(supportingUrl)
        const supportingQualityStartedAt =
          typeof performance !== 'undefined' ? performance.now() : Date.now()
        void measurePhotoQuality(compressionResult.file)
          .then((verdict) => {
            if (!verdict || verdict.ok || !verdict.reason) return
            // Staleness guard: a photo removed or reset while this measurement
            // was pending must not repopulate the warning map, or the next
            // analyze would emit an override for a warning that is not in the
            // current photo set.
            if (!supportingQualityUrlsRef.current.has(supportingUrl)) return
            const reason = verdict.reason
            setSupportingPhotoQuality((prev) => ({ ...prev, [supportingUrl]: reason }))

            // Impression for the slot label zip-code-step renders. Emitted
            // here rather than in the component because the state only keeps
            // the reason, and this is the one place the full metrics exist.
            const shownKey = `${attemptId}:${reason}:supporting:${supportingUrl}`
            if (supportingQualityShownKeysRef.current.has(shownKey)) return
            supportingQualityShownKeysRef.current.add(shownKey)
            const supportingMeasureMs =
              (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
              supportingQualityStartedAt
            // 2026-08-22: the warning UI was removed from the flow (it
            // intercepted users before the failed-result refinement gate).
            // The measurement stays silent: emit as capture_quality_measured
            // so the would-have-warned population remains tunable without
            // claiming an impression that never rendered.
            trackEvent('capture_quality_measured', {
              ...analysisFunnelEnvelope({
                funnelId: funnelIdRef.current,
                attemptId,
                mode: analysisMode,
                source: 'unknown',
                imageRoles: ['supporting'],
                authState: user ? 'authenticated' : 'anonymous',
                actualDestination: 'zip_step',
              }),
              attempt_id: attemptId,
              ...photoQualityAnalyticsProps({
                reason,
                metrics: verdict.metrics,
                role: 'supporting',
                measureMs: supportingMeasureMs,
              }),
              page_path: pathname ?? undefined,
            })
          })
          .catch(() => { /* fail-open by contract */ })
        const canonicalMetadata = data.imageMetadata ?? null
        analytics.grassIdPhotoUploaded(
          canonicalMetadata
            ? Math.round(canonicalMetadata.outputBytes / 1024)
            : Math.round(compressionResult.file.size / 1024),
          compressionResult.wasCompressed ? 'file-compressed' : 'file',
          {
            attemptId,
            source: 'file',
            mode: analysisMode,
            originalSizeKb: Math.round(compressionResult.originalSize / 1024),
            width: canonicalMetadata?.outputWidth ?? compressionResult.finalDimensions?.width,
            height: canonicalMetadata?.outputHeight ?? compressionResult.finalDimensions?.height,
            highFidelity: HIGH_FIDELITY_IMAGE_UPLOADS,
            imageRole: 'supporting',
            funnelId: funnelIdRef.current ?? undefined,
            originLawnId: options?.lawnContext?.lawnId ?? null,
            selectedLawnId: options?.lawnContext?.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
            fidelityFallbackReason: preparedImage.fidelityFallbackReason,
            clientPreparedSizeKb: Math.round(compressionResult.file.size / 1024),
            serverQuality: canonicalMetadata?.serverQuality,
            serverResized: canonicalMetadata?.wasResized,
          },
        )
      }

      setSupportingImages((current) => [...current, ...uploaded].slice(0, supportingPhotoSlots))
      trackEvent('refinement_photo_added', {
        ...analysisFunnelEnvelope({
          funnelId: funnelIdRef.current,
          attemptId,
          analysisId: rescanOfAnalysisIdRef.current ?? savedAnalysisId,
          selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
          mode: analysisMode,
          source: 'refinement_photo_slot',
          imageRoles: ['primary', 'supporting'],
          authState: user ? 'authenticated' : 'anonymous',
          actualDestination: 'analysis_input',
        }),
        mode: analysisMode,
        auth_state: user ? 'authenticated' : 'anonymous',
        image_count: 1 + supportingImages.length + uploaded.length,
        slot_index: supportingImages.length,
        guidance_shown: Boolean(supportingPhotoGuidance),
        photo_cap: photoCap,
        platform: 'web',
      })
      trackEvent('grass_id_guided_photos_added', {
        ...analysisFunnelEnvelope({
          funnelId: funnelIdRef.current,
          attemptId,
          selectedLawnId: options?.lawnContext?.lawnId ?? null,
          mode: analysisMode,
          imageRoles: ['primary', 'supporting'],
          authState: user ? 'authenticated' : 'anonymous',
          actualDestination: 'analysis_input',
        }),
        attempt_id: attemptId,
        added_count: uploaded.length,
        image_count: 1 + supportingImages.length + uploaded.length,
        mode: analysisMode,
      })
    } catch (error) {
      setSupportingImageError(error instanceof Error ? error.message : 'Supporting photos could not be uploaded.')
      if (uploaded.length > 0) {
        setSupportingImages((current) => [...current, ...uploaded].slice(0, supportingPhotoSlots))
      }
    } finally {
      setIsUploadingSupportingImages(false)
    }
  }, [analysisMode, isTrustedAnalysisImageUrl, newAttemptId, savedAnalysisId, supportingImages.length, options?.lawnContext?.lawnId, user, supportingPhotoSlots, supportingPhotoGuidance, photoCap, pathname])

  // Supporting warnings keep only the reason, so their metric buckets are
  // genuinely unknown rather than zero. Never report 0 as a measurement.
  const supportingQualityProps = useCallback((reason: PhotoQualityReason) => ({
    quality_reason: reason,
    photo_role: 'supporting' as PhotoRole,
    sharpness_bucket: 'unknown',
    mean_luma_bucket: 'unknown',
    luma_stddev_bucket: 'unknown',
    coarse_contrast_bucket: 'unknown',
    clipped_dark_ratio_bucket: 'unknown',
    clipped_bright_ratio_bucket: 'unknown',
    sampled_pixels: 0,
    measure_ms_bucket: 'unknown',
  }), [])

  const removeSupportingImage = useCallback((url: string) => {
    setSupportingImages((current) => current.filter((image) => image.url !== url))
    // Invalidate any still-pending measurement for this URL.
    supportingQualityUrlsRef.current.delete(url)
    const reason = supportingPhotoQuality[url]
    if (reason) {
      trackEvent('capture_quality_retake', {
        ...analysisFunnelEnvelope({
          funnelId: funnelIdRef.current,
          attemptId: attemptIdRef.current,
          mode: analysisMode,
          source: 'unknown',
          imageRoles: ['supporting'],
          authState: user ? 'authenticated' : 'anonymous',
          actualDestination: 'zip_step',
        }),
        attempt_id: attemptIdRef.current ?? undefined,
        ...supportingQualityProps(reason),
      })
    }
    setSupportingPhotoQuality((prev) => {
      if (!(url in prev)) return prev
      const next = { ...prev }
      delete next[url]
      return next
    })
  }, [analysisMode, user, supportingQualityProps, supportingPhotoQuality])

  // Analyze image handler. `modeOverride` lets cross-sell CTAs re-analyze the
  // current photo in a different mode in the same tick — setState-then-call
  // would read the stale `analysisMode` captured by this closure. Guarded
  // against non-mode values because this is also wired directly to event
  // handlers (onClick passes the event as the first arg).
  const analyzeImage = useCallback(async (
    modeOverride?: AnalysisMode | unknown,
    lawnContextOverride?: AnalysisLawnContext,
  ) => {
    const mode: AnalysisMode =
      modeOverride === 'identify' || modeOverride === 'diagnose'
        ? modeOverride
        : analysisMode
    // The real requirement is the uploaded URL (server-stored). `uploadedImage`
    // is the FileReader DataURL preview, set asynchronously after upload —
    // checking it created a race window where the URL existed and the UI
    // showed the ZIP step but Analyze silently no-op'd until the preview
    // landed. (Previously: `if (!uploadedImage) return`.)
    if (!uploadedImageUrl) {
      toast.error("Upload Error", { description: "Please upload an image first", position: "top-right", duration: 5000, closeButton: true })
      return
    }

    // Treatment lets the server infer location, including the no-location case.
    if (!skipZipStep && (!isZipCodeValid || !zipCode)) {
      setZipCodeError("Please enter a valid ZIP code to continue")
      return
    }

    if (!isTrustedAnalysisImageUrl(uploadedImageUrl)) {
      setUploadError("Image link is invalid or expired. Please upload your photo again.")
      return
    }

    // Single-flight: prevents Enter-then-click or any double-submit race
    // from kicking off two API calls.
    if (analyzeInFlightRef.current) return
    analyzeInFlightRef.current = true

    // The refund ledger is idempotent per attempt ID. Any run after a save or
    // server failure needs a fresh ID, whether the user taps the retry banner
    // or the normal analyze button below it.
    if (analysisSaveError || analysisServerError) {
      attemptIdRef.current = newAttemptId()
    }

    // Clear prior retry state so the recovery banner does not linger while
    // this run is in flight.
    setAnalysisNetworkError(false)
    setAnalysisSaveError(false)
    setAnalysisServerError(false)
    setIsAnalyzing(true)

    // Anticipatory pre-warm: the result's primary next step (for both anon and
    // authed users) is the 12-month timeline. Prefetch it now, during the
    // vision call, so the click after the result feels instant. Passive + best
    // effort — never block or throw into the analyze flow.
    try { router.prefetch?.('/timeline') } catch { /* prefetch unsupported */ }

    // Attempt id should already exist from handleImageUpload; mint a fallback
    // if somehow missing (e.g. URL-resume path that bypassed upload).
    if (!attemptIdRef.current) {
      attemptIdRef.current = newAttemptId()
    }
    const attemptId = attemptIdRef.current
    const funnelId = ensureFunnelId()
    const rescanOfAnalysisId = rescanOfAnalysisIdRef.current
    const imageUrls = [uploadedImageUrl, ...supportingImages.map((image) => image.url)]
    const analysisLawnContext = lawnContextOverride ?? {
      lawnId: options?.lawnContext?.lawnId,
      knownGrassType: options?.lawnContext?.knownGrassType,
      grassTypeProvenance: options?.lawnContext?.grassTypeProvenance,
      lawnContextIntent: options?.lawnContext?.lawnContextIntent,
    }
    analysisLawnContextRef.current = analysisLawnContext
    setPhotoCapGate(null)

    analytics.grassIdAnalysisStarted({
      attemptId,
      mode,
      funnelId,
      originLawnId: options?.lawnContext?.lawnId ?? null,
      selectedLawnId: analysisLawnContext.lawnId ?? null,
      imageRoles: imageUrls.map((_, index) => index === 0 ? 'primary' : 'supporting'),
      authState: user ? 'authenticated' : 'anonymous',
    })

    const startTime = Date.now()
    // True once the result has been committed to state (success path), so the
    // finally block doesn't redundantly flip isAnalyzing — the commit owns it.
    let committed = false
    // Set when the transient-abort branch schedules its one automatic retry, so
    // the finally block leaves `isAnalyzing` on across the backoff instead of
    // flashing the card back to the ZIP step and immediately into analyzing.
    let autoRetryScheduled = false
    let analysisFailureContext: Record<string, string | number | boolean | undefined> = {
      attempt_id: attemptId,
    }
    let analysisHttpStatus: number | undefined
    let analysisServerErrorCode: string | undefined

    try {
      // Call real AI analysis API
      const response = await fetch('/api/ai/analyze-grass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: uploadedImageUrl,
          ...(imageUrls.length > 1 ? { imageUrls } : {}),
          zipCode: zipCode,
          ...(skipZipStep && zipCode ? { zipSource: 'saved' } : {}),
          mode,
          attemptId,
          funnelId,
          // Browser PostHog distinct id so server-side captures (weed/identify
          // outcome events) land on the same person as the client funnel.
          distinctId: getPosthogDistinctId(),
          ...(rescanOfAnalysisId ? { rescanOfAnalysisId } : {}),
          // Ownership proof for the rescan exemption. Without it an anonymous
          // run that later signed in cannot claim its own root row, so the
          // free refinement would silently spend monthly quota.
          ...(rescanOfAnalysisId && resultAccessToken ? { sourceResultAccessToken: resultAccessToken } : {}),
          ...(refinementTriggerRef.current ? { refinementTrigger: refinementTriggerRef.current } : {}),
          ...(analysisLawnContext?.lawnId
            ? { lawnId: analysisLawnContext.lawnId }
            : {}),
          ...(analysisLawnContext?.knownGrassType
            ? { knownGrassType: analysisLawnContext.knownGrassType }
            : {}),
          ...(analysisLawnContext?.grassTypeProvenance
            ? { grassTypeProvenance: analysisLawnContext.grassTypeProvenance }
            : {}),
          ...(analysisLawnContext?.lawnContextIntent
            ? { lawnContextIntent: analysisLawnContext.lawnContextIntent }
            : {}),
        }),
      })

      // Parse defensively — a framework-level 500 (OpenAI timeout bubbling up
      // as a function timeout) returns a non-JSON body that response.json()
      // would choke on. See lib/safe-json.
      const data = (await safeJson<{
        success?: boolean
        result?: any
        location?: { zip?: unknown; source?: unknown; placeLabel?: unknown }
        analysisId?: unknown
        pipelineVersion?: unknown
        resultAccessToken?: unknown
        signupRequired?: boolean
        tier?: 'anon' | 'email'
        upgradeRequired?: boolean
        usage?: {
          remaining?: number
          limit?: number
          isPremium?: boolean
          exemption?: 'identify_to_weed' | 'same_mode_refinement'
          rescanExempt?: boolean
        }
        refinement?: RefinementPolicy
        photoLimit?: PhotoLimitDetail
        error?: unknown
        code?: unknown
        details?: {
          error?: {
            code?: unknown
            type?: unknown
          }
        }
      }>(response)) ?? {}

      if (!response.ok) {
        const detailsError = data.details?.error
        analysisHttpStatus = response.status
        analysisServerErrorCode =
          typeof data.code === 'string'
            ? data.code
            : typeof detailsError?.code === 'string'
              ? detailsError.code
              : typeof detailsError?.type === 'string'
                ? detailsError.type
                : undefined
        analysisFailureContext = {
          attempt_id: attemptId,
          http_status: analysisHttpStatus,
          server_error_code: analysisServerErrorCode,
        }
        // Photo-count denial, not a quota denial. It must be read BEFORE the
        // signupRequired/upgradeRequired branches: those render the monthly
        // wall, whose copy ("that was your free grass ID for the month") is
        // simply untrue here. The 403 body deliberately carries neither key.
        if (analysisServerErrorCode === 'photo_limit_reached') {
          const limit = data.photoLimit
          const requiredTier: Exclude<PhotoTier, 'anonymous'> =
            limit?.requiredTier === 'premium' ? 'premium' : 'registered_free'
          trackEvent('refinement_gate_shown', {
            ...analysisFunnelEnvelope({
              funnelId,
              attemptId,
              analysisId: rescanOfAnalysisId,
              selectedLawnId: analysisLawnContext.lawnId ?? null,
              mode,
              source: 'analysis_photo_cap',
              authState: user ? 'authenticated' : 'anonymous',
              actualDestination: 'refinement_signin_gate',
            }),
            mode,
            trigger: 'photo_cap',
            auth_state: user ? 'authenticated' : 'anonymous',
            image_count: imageUrls.length,
            photo_cap: limit?.cap ?? null,
            photo_limit_reason: limit?.reason ?? null,
            required_tier: requiredTier,
            locked: true,
            platform: 'web',
          })
          setPhotoCapGate({
            intent: 'photo_cap',
            requiredTier,
            mode,
            cap: limit?.cap ?? imageUrls.length - 1,
            requested: limit?.requested ?? imageUrls.length,
            denialReason: limit?.reason ?? null,
            reason: refinementTriggerRef.current === 'photo_cap'
              ? null
              : refinementTriggerRef.current,
          })
          setIsAnalyzing(false)
          return
        }
        if (data?.signupRequired) {
          // Older deploys omit `tier`; treat a missing value as the no-account
          // rung so the email lane still renders rather than silently vanishing.
          setLimitTier(data.tier === 'email' ? 'email' : 'anon')
          if (data.tier === 'email') {
            markEmailUnlockHint()
            setEmailTierObserved(true)
          }
          // Save progress so it can be restored after OAuth signup
          const analysisResumeId = newAnalysisResumeId()
          const entryPath = pathname === '/diagnose' ? '/diagnose' : '/'
          analysisResumeFlowRef.current = { id: analysisResumeId, mode, entryPath }
          setQuotaResumeId(analysisResumeId)
          try {
            localStorage.setItem('pendingAnalysis_limit', JSON.stringify({
              imageUrl: uploadedImageUrl,
              imageUrls,
              zipCode,
              analysisMode: mode,
              resumeAnalysis: true,
              analysisResumeId,
              entryPath,
              savedAt: Date.now(),
            }))
          } catch { /* localStorage unavailable */ }
          analytics.analysisLimitReached({
            mode,
            reason: data.tier === 'email'
              ? 'email_tier_quota_exhausted'
              : 'anon_quota_exhausted',
            surface: `${surfaceFromPathname(pathname)}:analysis_limit_wall`,
            analysisResumeId,
            entryPath,
            imageCount: imageUrls.length,
            attemptId,
            funnelId,
            // setEmailTierObserved only reaches the ambient rung on the next
            // effect pass, so this event would otherwise say
            // email_tier_quota_exhausted while user_tier still said anonymous.
            // `signupRequired` is the signed-out lane by definition.
            userTier: data.tier
              ? resolveAnalyticsUserTier({
                  isAuthenticated: false,
                  emailUnlocked: data.tier === 'email',
                })
              : undefined,
          })
          analytics.grassIdLimitReached({
            attemptId,
            mode,
            funnelId,
            selectedLawnId: analysisLawnContext.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
          })
          // Remember the exhaustion for this mode + month so the NEXT visit
          // can surface the wall before the photo/ZIP effort, not after. This
          // attempt was genuinely walled, so it earns the notice.
          writeAnonQuotaReason(mode, 'blocked')
          setAnonQuotaReason('blocked')
          setLimitReached(true)
          setIsAnalyzing(false)
          return
        }
        if (data?.upgradeRequired) {
          if (analysisResumeFlowRef.current) {
            trackEvent('analysis_resume_blocked', {
              analysis_resume_id: analysisResumeFlowRef.current.id,
              mode,
              entry_path: analysisResumeFlowRef.current.entryPath,
              reason: 'registered_quota_exhausted',
            })
            analysisResumeFlowRef.current = null
          }
          trackEvent('analysis_upgrade_required', { mode })
          analytics.grassIdUpgradeRequired({
            attemptId,
            mode,
            funnelId,
            selectedLawnId: analysisLawnContext.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
          })
          setUpgradeRequired(true)
          setIsAnalyzing(false)
          return
        }
        throw new Error(errorMessageFrom(data?.error, `Analysis failed (${response.status})`))
      }

      if (data.success && data.result) {
        let analysisZip = zipCode
        if (skipZipStep) {
          const location = data.location
          const resolvedZip = typeof location?.zip === 'string' && /^\d{5}$/.test(location.zip)
            && location.zip !== '00000' ? location.zip : null
          setZipCode(resolvedZip ?? '')
          analysisZip = resolvedZip ?? ''
          setIsZipCodeValid(Boolean(resolvedZip))
          trackEvent('scan_location_resolved', {
            attempt_id: attemptId, funnel_id: funnelId, mode,
            zip_source: typeof location?.source === 'string' ? location.source : 'none', has_location: Boolean(resolvedZip),
          })
        }
        if (analysisResumeFlowRef.current) {
          trackEvent('analysis_resume_completed', {
            analysis_resume_id: analysisResumeFlowRef.current.id,
            mode,
            entry_path: analysisResumeFlowRef.current.entryPath,
            outcome: 'success',
          })
          analysisResumeFlowRef.current = null
          setQuotaResumeId(null)
        }
        const routeAnalysisId = typeof data.analysisId === 'string' ? data.analysisId : undefined
        const pipelineVersion = typeof data.result?.pipelineVersion === 'string'
          ? data.result.pipelineVersion
          : typeof data.pipelineVersion === 'string'
            ? data.pipelineVersion
            : undefined
        // Quota awareness — the route returns usage on every success but the
        // client previously dropped it. remaining===1 drives the soft
        // "1 free ID left" banner on the result (ask on the way up, not at
        // the wall). Premium reports Infinity via a huge number server-side.
        if (data.usage && typeof data.usage.remaining === 'number') {
          setScanUsage({
            remaining: data.usage.remaining,
            limit: typeof data.usage.limit === 'number' ? data.usage.limit : 3,
            isPremium: Boolean(data.usage.isPremium),
          })
          // A signed-out browser served the email-tier ceiling is holding an
          // EMAIL_UNLOCK_COOKIE it cannot read for itself.
          if (!user && anonymousLimitImpliesEmailTier(mode, data.usage.limit)) {
            markEmailUnlockHint()
            setEmailTierObserved(true)
          }
          // A successful run means the quota wasn't actually exhausted (new
          // month, allowlisted IP, or a signed-in session) — drop the stale
          // pre-upload notice for this mode.
          if (data.usage.remaining > 0 || data.usage.isPremium) {
            clearAnonQuotaReason(mode)
            setAnonQuotaReason(null)
          } else if (!user) {
            // This success consumed the last free run for the month. Record it
            // as `consumed`, NOT `blocked`: the user was never walled, so the
            // pre-upload notice must stay silent for them (with an anon limit
            // of 1 that population is everyone who just completed a scan —
            // flagging it as exhaustion turned the notice into a broadcast).
            // The state still exists so a later blocked attempt, or a surface
            // that wants the softer signal, can reason about it.
            writeAnonQuotaReason(mode, 'consumed')
            setAnonQuotaReason('consumed')
          }
        }

        const prefersReducedMotion =
          typeof window !== 'undefined' &&
          window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

        // Min-display gate — hold the "analyzing" narration long enough that the
        // show-the-work steps land instead of flashing past on a fast response
        // (labor perception, per the growth-design playbook; mirrors the mobile
        // BuildingPlanOverlay). Success path only — errors/limits return earlier
        // and are never delayed. Skipped under reduced-motion (users who opt out
        // of motion want speed, not pacing).
        {
          const minDisplayMs = prefersReducedMotion ? 0 : 2500
          const elapsed = Date.now() - startTime
          if (elapsed < minDisplayMs) {
            await new Promise((resolve) => setTimeout(resolve, minDisplayMs - elapsed))
          }
        }

        // Commit the analyzing→result swap through the View Transitions API so
        // the user's photo morphs from the scanning frame into the result hero
        // (shared `view-transition-name: grass-photo`). flushSync forces the DOM
        // to update inside the transition callback. Falls back to a plain swap
        // when the API is unsupported or motion is reduced. `committed` guards
        // the finally block from a redundant setIsAnalyzing(false).
        const startViewTransition =
          typeof document !== 'undefined'
            ? (document as Document & {
                startViewTransition?: (cb: () => void) => unknown
              }).startViewTransition
            : undefined
        const commitResult = (apply: () => void) => {
          if (!prefersReducedMotion && typeof startViewTransition === 'function') {
            const transition = startViewTransition.call(document, () => flushSync(apply)) as
              | { finished?: Promise<unknown>; ready?: Promise<unknown> }
              | undefined
            // An aborted/skipped transition (tab hidden, a competing transition
            // from the /result URL rewrite, rapid re-render) rejects these
            // promises with InvalidStateError. The DOM update inside the
            // callback still commits, so the abort is benign — swallow it
            // instead of surfacing an unhandled rejection in the console.
            transition?.finished?.catch?.(() => {})
            transition?.ready?.catch?.(() => {})
          } else {
            apply()
          }
          committed = true
        }

        // Analysis succeeded — clear the persisted flow state
        try { localStorage.removeItem('pendingAnalysis_flow') } catch { /* noop */ }

        // Handle diagnosis mode results
        if (mode === 'diagnose' && data.result.mode === 'diagnosis') {
          const diagResult: DiagnosisResult = {
            ...data.result,
            image: uploadedImageUrl,
          }
          commitResult(() => {
            setDiagnosisResult(diagResult)
            setIsAnalyzing(false)
          })

          // Save analysisId for everyone (server persisted for both auth and
          // anon as of the 2026-05-02 audit-v2 follow-up). Anon rows have a
          // 30-day TTL via the cleanup-anon-analyses cron.
          {
            if (routeAnalysisId) {
              setSavedAnalysisId(routeAnalysisId)
            }
          }
          // Diagnose rows use the same signed anonymous-result capability as
          // Identify and Weed. Keep the fresh token in hook state so an OAuth
          // handoff can claim this exact row instead of restoring an ID that
          // the claim endpoint must reject.
          setResultAccessToken(typeof data.resultAccessToken === 'string' ? data.resultAccessToken : null)

          // One canonical terminal event. The old grass_id_completed event had
          // identical semantics and counts, which made funnels double-count a
          // single result.
          const processingTime = Date.now() - startTime
          const diagGrassType = `diagnosis:${diagResult.problems?.[0]?.name || 'unknown'}`
          // Scroll-independent counterpart to grass_id_result_viewed — fires
          // on API success regardless of scroll/observer state, so the delta
          // surfaces hydration crashes and instrumentation losses.
          analytics.grassIdResultRendered({
            attemptId,
            mode,
            grassType: diagGrassType,
            confidence: diagResult.confidence,
            analysisId: routeAnalysisId,
            model: data.result?.model,
            pipelineVersion,
            funnelId,
            originLawnId: options?.lawnContext?.lawnId ?? null,
            selectedLawnId: analysisLawnContext.lawnId ?? null,
            imageRoles: imageUrls.map((_, index) => index === 0 ? 'primary' : 'supporting'),
            displayConfidence: diagResult.confidence,
            safetyProof: data.result?.hostGrassVerified === true
              ? 'host_grass_user_verified'
              : 'host_grass_unverified',
            promptVersion: data.result?.quality?.promptVersion ?? data.result?.promptVersion,
            promptStrategy: data.result?.promptStrategy,
            authState: user ? 'authenticated' : 'anonymous',
            actualDestination: 'diagnosis_result',
            processingTimeMs: processingTime,
            userType: user ? 'authenticated' : 'anonymous',
            imageQuality: data.result?.imageQuality,
            imageCount: imageUrls.length,
            rescanOfAnalysisId: rescanOfAnalysisId ?? undefined,
            hostGrassVerified: data.result?.hostGrassVerified === true,
            imageWidth: primaryImageMetricsRef.current.width,
            imageHeight: primaryImageMetricsRef.current.height,
            originalSizeKb: primaryImageMetricsRef.current.originalSizeKb,
            uploadedSizeKb: primaryImageMetricsRef.current.uploadedSizeKb,
          })
        } else {
          // Convert AI response to our AnalysisResult format (identify mode)
          const aiResult: AnalysisResult = {
            grassType: data.result.grassType || "Unknown",
            subjectGuess: data.result.subjectGuess,
            abstentionReason: data.result.abstentionReason,
            identificationStatus: data.result.identificationStatus,
            missingEvidence: Array.isArray(data.result.missingEvidence) ? data.result.missingEvidence : undefined,
            recommendedNextPhoto: typeof data.result.recommendedNextPhoto === 'string'
              ? data.result.recommendedNextPhoto
              : null,
            confidence: typeof data.result.confidence === 'number'
              ? data.result.confidence
              : 50,
            displayConfidence: typeof data.result.displayConfidence === 'number'
              ? data.result.displayConfidence
              : undefined,
            candidates: Array.isArray(data.result.candidates) ? data.result.candidates : undefined,
            recovery: data.result.recovery?.version === 'identify-recovery-v1'
              ? data.result.recovery
              : undefined,
            weedGuess: data.result.weedGuess ?? undefined,
            possibleWeeds: Array.isArray(data.result.possibleWeeds) ? data.result.possibleWeeds : undefined,
            visibleEvidence: Array.isArray(data.result.visibleEvidence) ? data.result.visibleEvidence : undefined,
            quality: data.result.quality,
            tips: data.result.tips || ["General care recommended"],
            region: data.result.region || "Analysis needed",
            image: uploadedImageUrl,
            alternatives: data.result.alternatives || [],
            analysis: data.result.analysis,
            seasonalNotes: data.result.seasonalNotes,
            imageQuality: data.result.imageQuality || "Unknown",
            model: typeof data.result.model === 'string' ? data.result.model : undefined,
            promptStrategy: typeof data.result.promptStrategy === 'string' ? data.result.promptStrategy : undefined,
            pipelineVersion,
            identitySource: 'model',
          }

          // Check if this is a successful, confident result. Failure detection
          // stays on RAW confidence (0 is the failure sentinel); band checks
          // use the server-gated display value when present.
          const isFailedAnalysis = aiResult.confidence === 0 ||
            aiResult.grassType.includes('Issue') ||
            aiResult.grassType.includes('Unclear') ||
            aiResult.grassType.includes('Unavailable') ||
            aiResult.grassType.includes('Not a Grass Image')
          const isLowConfidence = (aiResult.displayConfidence ?? aiResult.confidence) < 50

          // Keep the server row id for every persisted outcome, including an
          // uncertain one. It is the capability needed for correction and a
          // linked quota-exempt new-photo rescan.
          if (routeAnalysisId) {
            setSavedAnalysisId(routeAnalysisId)
          }
          setResultAccessToken(typeof data.resultAccessToken === 'string' ? data.resultAccessToken : null)

          // Save and surface a shareable URL when the result is good. The
          // primary path (server-returned analysisId) works for both auth and
          // anon users. The /api/user/save-analysis fallback is auth-only —
          // anon never reaches it because the primary path always succeeds
          // when the server persists, which it now does for anon too.
          if (!isFailedAnalysis && !isLowConfidence) {
            if (routeAnalysisId) {
              localStorage.removeItem('pendingAnalysis')
            } else if (user) {
              try {
                // Fallback path: persist explicitly when the server didn't
                // return an ID. Auth-only since /api/user/save-analysis
                // requires a session.
                const saveResponse = await fetch('/api/user/save-analysis', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    grassType: aiResult.grassType,
                    confidence: aiResult.confidence,
                    zipCode: analysisZip,
                    imageUrl: uploadedImageUrl,
                    analysisResult: aiResult
                  })
                })

                if (saveResponse.ok) {
                  const saveData = await saveResponse.json()
                  if (saveData.analysisId) {
                    setSavedAnalysisId(saveData.analysisId)
                  }
                }

                localStorage.removeItem('pendingAnalysis')
              } catch (saveError) {
                console.error('Failed to save analysis:', saveError)
                // Continue anyway - show results even if save failed
              }
            }
          }

          commitResult(() => {
            setAnalysisResult(aiResult)
            setIsAnalyzing(false)
          })

          // Track the single canonical terminal result event.
          const processingTime = Date.now() - startTime
          const candidateMargin = aiResult.candidates && aiResult.candidates.length >= 2
            ? Math.round((aiResult.candidates[0].probability - aiResult.candidates[1].probability) * 1000) / 1000
            : undefined
          // Scroll-independent counterpart to grass_id_result_viewed — fires
          // on API success regardless of scroll/observer state, so the delta
          // surfaces hydration crashes and instrumentation losses.
          analytics.grassIdResultRendered({
            attemptId,
            mode,
            grassType: aiResult.grassType,
            confidence: aiResult.confidence,
            analysisId: routeAnalysisId,
            model: aiResult.model,
            pipelineVersion: aiResult.pipelineVersion,
            funnelId,
            originLawnId: options?.lawnContext?.lawnId ?? null,
            selectedLawnId: analysisLawnContext.lawnId ?? null,
            imageRoles: imageUrls.map((_, index) => index === 0 ? 'primary' : 'supporting'),
            displayConfidence: aiResult.displayConfidence ?? aiResult.confidence,
            gatingReasons: Array.isArray(data.result?.confidenceGating)
              ? data.result.confidenceGating
              : null,
            safetyProof: aiResult.identitySource === 'user_confirmed'
              ? 'user_confirmed_identity'
              : 'model_identity',
            promptVersion: aiResult.quality?.promptVersion ?? data.result?.promptVersion,
            promptStrategy: data.result?.promptStrategy,
            authState: user ? 'authenticated' : 'anonymous',
            actualDestination: 'identify_result',
            processingTimeMs: processingTime,
            userType: user ? 'authenticated' : 'anonymous',
            imageQuality: aiResult.imageQuality,
            isUnknownOther: aiResult.grassType === 'Unknown/Other',
            abstentionReason: aiResult.abstentionReason ?? undefined,
            candidateMargin,
            imageCount: imageUrls.length,
            rescanOfAnalysisId: rescanOfAnalysisId ?? undefined,
            imageWidth: primaryImageMetricsRef.current.width,
            imageHeight: primaryImageMetricsRef.current.height,
            originalSizeKb: primaryImageMetricsRef.current.originalSizeKb,
            uploadedSizeKb: primaryImageMetricsRef.current.uploadedSizeKb,
          })
        }

        // Server's refinement entitlement for THIS result. Consumers prefer it
        // over their local predicate whenever it is present.
        setRefinementPolicy(data.refinement ?? null)

        // Outcome delta — only when this run was an explicit refinement of a
        // previous result, so a plain first scan never emits it.
        const previousOutcome = previousOutcomeRef.current
        previousOutcomeRef.current = null
        const newOutcome: AnalysisOutcomeLabel = mode === 'diagnose'
          ? diagnoseOutcomeLabel(data.result as DiagnosisResult)
          : identifyOutcomeLabel(data.result as AnalysisResult)
        const newConfidence = mode === 'diagnose'
          ? (typeof data.result?.quality?.displayConfidence === 'number'
            ? data.result.quality.displayConfidence
            : typeof data.result?.confidence === 'number' ? data.result.confidence : null)
          : (typeof data.result?.displayConfidence === 'number'
            ? data.result.displayConfidence
            : typeof data.result?.confidence === 'number' ? data.result.confidence : null)
        if (previousOutcome && rescanOfAnalysisId) {
          trackEvent('refinement_outcome_delta', {
            ...analysisFunnelEnvelope({
              funnelId,
              attemptId,
              analysisId: rescanOfAnalysisId,
              selectedLawnId: analysisLawnContext.lawnId ?? null,
              mode,
              source: 'refinement_result',
              imageRoles: imageUrls.map((_, index) => index === 0 ? 'primary' : 'supporting'),
              displayConfidence: newConfidence,
              authState: user ? 'authenticated' : 'anonymous',
              actualDestination: 'refined_result',
            }),
            mode,
            previous_outcome: previousOutcome.outcome,
            new_outcome: newOutcome,
            previous_confidence: previousOutcome.confidence,
            new_confidence: newConfidence,
            image_count: imageUrls.length,
            auth_state: user ? 'authenticated' : 'anonymous',
            rescan_exempt: Boolean(data.usage?.rescanExempt),
            trigger: refinementTriggerRef.current,
            platform: 'web',
          })
          setRefinementSummary({
            imageCount: imageUrls.length,
            previousOutcome: previousOutcome.outcome,
            newOutcome,
            previousConfidence: previousOutcome.confidence,
            newConfidence,
          })
        } else {
          setRefinementSummary(null)
        }
        refinementTriggerRef.current = null
        rescanOfAnalysisIdRef.current = null
      } else {
        throw new Error('Invalid response')
      }
    } catch (error) {
      console.error('Analysis error:', error)

      // Detect the transient-network-abort class. iOS Safari kills an in-flight
      // fetch when the tab is backgrounded mid-call and surfaces it as a
      // `TypeError: Load failed` (no stack frames); other browsers say "Failed
      // to fetch" / "NetworkError"; an explicit cancel raises `AbortError`.
      // None of these mean the photo or the server is bad — the request simply
      // never completed — so they get a recoverable retry against the same
      // already-uploaded image instead of the generic "Analysis Failed" card.
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      const lowerMsg = errorMsg.toLowerCase()

      if (isTransientNetworkError(error)) {
        // Recoverable: keep uploadedImageUrl + zipCode intact, surface the
        // retry banner, and DON'T pollute Error Tracking — this is an expected
        // mobile-connectivity event, not a bug. Tagged distinctly so the
        // failure funnel can separate connection drops from real failures.
        analytics.grassIdAnalysisFailed({
          attemptId,
          mode,
          errorCategory: 'network_aborted',
          errorMessage: errorMsg.slice(0, 200),
          funnelId,
          selectedLawnId: analysisLawnContext.lawnId ?? null,
          authState: user ? 'authenticated' : 'anonymous',
          retryOffered: true,
        })

        // Post-OAuth resume is the one place this abort is near-certain (iOS
        // Safari kills in-flight requests right after the redirect) and the one
        // place a manual retry costs the most — the user already signed in to
        // get this result. Retry once, automatically, instead of asking them to
        // tap a banner they have to notice first.
        //
        // Idempotency: the retry reuses the SAME attemptId, and the analyze
        // route has no attemptId-keyed dedupe. It does not need one here — the
        // route's deterministic-rescan cache (analyze-grass route, "Deterministic
        // rescan cache") runs BEFORE quota is consumed and serves a settled
        // prior for the same image hash + mode + owner verbatim, with no model
        // call and no quota burn. So if the first attempt did reach the server
        // and persist, the retry is free; if it did not, the retry is the
        // correct fresh run. This is the same exposure the manual retry banner
        // (retryAnalysis) has always had, capped here at exactly one automatic
        // attempt per resume flow.
        const resumeFlow = analysisResumeFlowRef.current
        if (resumeFlow && autoRetriedResumeIdRef.current !== resumeFlow.id) {
          autoRetriedResumeIdRef.current = resumeFlow.id
          analytics.grassIdRetryStarted({
            previousAttemptId: attemptId,
            fromResult: false,
            mode,
            auto: true,
            funnelId,
            selectedLawnId: analysisLawnContext.lawnId ?? null,
            authState: user ? 'authenticated' : 'anonymous',
          })
          // Deferred past `finally` so the single-flight ref is released before
          // the retry runs. No toast and no banner on this pass — the user sees
          // an uninterrupted "analyzing" state. A second transient failure
          // finds the guard already set and falls through to the banner below.
          autoRetryScheduled = true
          setTimeout(() => { void analyzeImageRef.current?.(mode, lawnContextOverride) }, 1500)
          return
        }

        toast.error("Connection dropped", { description: "Your connection dropped before we finished. Tap to try again.", position: "top-right", duration: 5000, closeButton: true })
        setAnalysisNetworkError(true)
        // isAnalyzing is reset in finally (committed is false here), which
        // returns the card to the ZIP step where the recovery banner renders.
        return
      }

      if (analysisServerErrorCode === 'analysis_persist_failed') {
        toast.error("Result wasn't saved", {
          description: "Your photo is still here, and this scan was not counted. Tap to try again.",
          position: "top-right",
          duration: 5000,
          closeButton: true,
        })
        setAnalysisSaveError(true)
        performanceAnalytics.captureError({
          error,
          source: 'api',
          severity: 'error',
          context: { error_type: 'persistence_failed', stage: 'save_analysis', ...analysisFailureContext },
        })
        analytics.grassIdAnalysisFailed({
          attemptId,
          mode,
          errorCategory: 'persistence_failed',
          errorMessage: errorMsg.slice(0, 200),
          httpStatus: analysisHttpStatus,
          serverErrorCode: analysisServerErrorCode,
          funnelId,
          selectedLawnId: analysisLawnContext.lawnId ?? null,
          authState: user ? 'authenticated' : 'anonymous',
          retryOffered: true,
        })
        return
      }

      // 4xx rejections carry a user-actionable message from the server (e.g.
      // the 422 image_unreadable copy) — show it instead of the generic line.
      const isActionableStatus =
        typeof analysisHttpStatus === 'number' &&
        analysisHttpStatus >= 400 &&
        analysisHttpStatus < 500

      // Everything that is not a 4xx rejection is a server-side failure: a
      // 5xx (analysis_timeout, openai_upstream_unavailable, openai_api_error,
      // internal_server_error, a platform 504), a non-JSON body, or an
      // unusable success body. The photo is fine, so keep it and offer the
      // same-photo retry instead of a placeholder result that asks for a new
      // photo. A 4xx keeps the placeholder path: its message names the fix.
      if (!isActionableStatus && !committed) {
        const serverErrorCategory = analysisServerErrorCode === 'analysis_timeout'
          ? 'analysis_timeout'
          : lowerMsg.includes('invalid response')
            ? 'invalid_response'
            : 'server_error'
        performanceAnalytics.captureError({
          error,
          source: 'api',
          severity: 'error',
          context: { error_type: serverErrorCategory, stage: 'analyze_image', ...analysisFailureContext },
        })
        analytics.grassIdAnalysisFailed({
          attemptId,
          mode,
          errorCategory: serverErrorCategory,
          errorMessage: errorMsg.slice(0, 200),
          httpStatus: analysisHttpStatus,
          serverErrorCode: analysisServerErrorCode,
          funnelId,
          selectedLawnId: analysisLawnContext.lawnId ?? null,
          authState: user ? 'authenticated' : 'anonymous',
          retryOffered: true,
        })
        if (analysisResumeFlowRef.current) {
          if (isOAuthRedirectReplacing()) {
            trackEvent('analysis_resume_page_unloaded', {
              analysis_resume_id: analysisResumeFlowRef.current.id,
              mode,
              entry_path: analysisResumeFlowRef.current.entryPath,
              reason: serverErrorCategory,
            })
          } else {
            trackEvent('analysis_resume_failed', {
              analysis_resume_id: analysisResumeFlowRef.current.id,
              mode,
              entry_path: analysisResumeFlowRef.current.entryPath,
              reason: serverErrorCategory,
              http_status: analysisHttpStatus,
            })
          }
          analysisResumeFlowRef.current = null
          setQuotaResumeId(null)
          setQuotaResumePending(false)
        }
        if (!isOAuthRedirectReplacing()) {
          toast.error("The analysis didn't finish", {
            description: serverFaultMessage(analysisHttpStatus, analysisServerErrorCode)
              ?? "Your photo is still here. Tap Try again.",
            position: "top-right",
            duration: 5000,
            closeButton: true,
          })
        }
        setAnalysisServerError(true)
        return
      }
      // Known server faults carry their own actionable sentence; unknown 5xx
      // stays on the generic line.
      const serverFaultCopy = serverFaultMessage(analysisHttpStatus, analysisServerErrorCode)
      // The document is already committed to AuthContext's pending-redirect
      // replace: this rejection is the teardown killing our own fetch, and the
      // toast would flash on a page that is being thrown away.
      if (!isOAuthRedirectReplacing()) {
        toast.error("Analysis Failed", {
          description: isActionableStatus && errorMsg && !errorMsg.startsWith('Analysis failed (')
            ? errorMsg
            : serverFaultCopy ?? "Could not analyze image. Please try again.",
          position: "top-right",
          duration: 5000,
          closeButton: true,
        })
      }

      // Track error for monitoring
      const errorCategory = lowerMsg.includes('timeout')
        ? 'openai_timeout'
        : lowerMsg.includes('invalid response')
          ? 'invalid_response'
          : 'analysis_failed'
      // Client-actionable server rejections (422 image_unreadable "We couldn't
      // read that photo…" and other 4xx short of a server fault) are expected
      // product outcomes, not crashes — keep them out of Error Tracking like
      // the transient-network and ImageCompressionError classes above. They
      // still flow to grass_id_analysis_failed below with full context.
      if (!isActionableStatus) {
        // Track to PostHog Error Tracking via captureException. The richer
        // category is preserved on grass_id_analysis_failed below.
        performanceAnalytics.captureError({
          error,
          source: 'api',
          severity: 'error',
          context: { error_type: errorCategory, stage: 'analyze_image', ...analysisFailureContext },
        })
      }
      analytics.grassIdAnalysisFailed({
        attemptId,
        mode,
        errorCategory,
        errorMessage: errorMsg.slice(0, 200),
        httpStatus: analysisHttpStatus,
        serverErrorCode: analysisServerErrorCode,
        funnelId,
        selectedLawnId: analysisLawnContext.lawnId ?? null,
        authState: user ? 'authenticated' : 'anonymous',
        retryOffered: false,
      })
      if (analysisResumeFlowRef.current) {
        // A resume killed by AuthContext's own pending-redirect replace never
        // failed: the document is leaving and the fetch died at teardown. It
        // reported as 'Unknown error' and showed a failure toast on a page the
        // user never saw finish. Terminal, but not a failure.
        if (isOAuthRedirectReplacing()) {
          trackEvent('analysis_resume_page_unloaded', {
            analysis_resume_id: analysisResumeFlowRef.current.id,
            mode,
            entry_path: analysisResumeFlowRef.current.entryPath,
            reason: errorCategory,
          })
        } else {
          trackEvent('analysis_resume_failed', {
            analysis_resume_id: analysisResumeFlowRef.current.id,
            mode,
            entry_path: analysisResumeFlowRef.current.entryPath,
            reason: errorCategory,
            http_status: analysisHttpStatus,
          })
        }
        analysisResumeFlowRef.current = null
        // The flow ref is the only thing that could have cleared these, so a
        // dead resume used to leave its id live for the rest of the session and
        // every later OAuth hop rebuilt a quota-resume URL from it.
        setQuotaResumeId(null)
        setQuotaResumePending(false)
      }

      // Fallback to error result — mode-aware
      if (mode === 'diagnose') {
        const fallbackDiagnosis: DiagnosisResult = {
          mode: 'diagnosis',
          problems: [],
          overallAssessment: 'Analysis could not be completed. Please try uploading a clearer photo of the affected area.',
          immediateActions: [],
          treatmentRecommendations: [],
          preventionTips: [],
          imageQuality: 'Unable to determine',
          confidence: 0,
          region: 'Unable to determine',
          image: uploadedImageUrl || '',
        }
        setDiagnosisResult(fallbackDiagnosis)
      } else {
        const fallbackResult: AnalysisResult = {
          grassType: "Analysis Failed",
          confidence: 0,
          tips: ["Please try uploading a clearer image", "Ensure good lighting", "Include multiple grass blades"],
          region: "Unable to determine",
          image: uploadedImageUrl,
          alternatives: ["Try again with better image"],
        }
        setAnalysisResult(fallbackResult)
      }
    } finally {
      // Success commits set isAnalyzing(false) inside the view-transition so the
      // morph captures the swap; only flip here for the non-committed paths
      // (errors, limit/upgrade already returned).
      if (!committed && !autoRetryScheduled) setIsAnalyzing(false)
      analyzeInFlightRef.current = false
    }
  }, [
    uploadedImageUrl,
    supportingImages,
    zipCode,
    isZipCodeValid,
    user,
    analysisMode,
    isTrustedAnalysisImageUrl,
    newAttemptId,
    ensureFunnelId,
    router,
    pathname,
    analysisSaveError,
    analysisServerError,
    options?.lawnContext?.lawnId,
    options?.lawnContext?.knownGrassType,
    options?.lawnContext?.grassTypeProvenance,
    options?.lawnContext?.lawnContextIntent,
    resultAccessToken,
    photoQuality,
    supportingPhotoQuality,
    supportingQualityProps,
    skipZipStep,
  ])

  analyzeImageRef.current = analyzeImage

  useEffect(() => {
    if (!skipZipStep || !uploadedImageUrl || isUploading || isAnalyzing || isOAuthRedirectReplacing()) return
    if (autoStartPhotoRef.current !== uploadedImageUrl) return
    autoStartPhotoRef.current = null
    trackEvent('zip_step_skipped', {
      attempt_id: attemptIdRef.current, funnel_id: funnelIdRef.current, mode: analysisMode,
    })
    void analyzeImage()
  }, [skipZipStep, uploadedImageUrl, isUploading, isAnalyzing, analysisMode, analyzeImage])

  // A quota wall is a continuation, not a destination. Once OAuth restores the
  // authenticated session and the persisted photo/ZIP are back in state, run
  // exactly the analysis the user already requested. Clear the pending bit
  // before dispatch so a 429 or rerender cannot create an auto-run loop.
  useEffect(() => {
    const resumeFlow = analysisResumeFlowRef.current
    if (
      !user ||
      !quotaResumePending ||
      !resumeFlow ||
      !uploadedImageUrl ||
      (!skipZipStep && (!zipCode || !isZipCodeValid)) ||
      isAnalyzing ||
      analysisResult ||
      diagnosisResult ||
      // This document is on its way out through AuthContext's pending-redirect
      // replace. Starting the resume here spends the run twice: the fetch dies
      // at teardown and the replaced document re-initialises the same flow from
      // the URL and runs it again, so analysis_resume_started double-fired for
      // over half of all resumers.
      isOAuthRedirectReplacing()
    ) return

    setQuotaResumePending(false)
    setLimitReached(false)
    setUpgradeRequired(false)
    trackEvent('analysis_resume_started', {
      analysis_resume_id: resumeFlow.id,
      mode: resumeFlow.mode,
      entry_path: resumeFlow.entryPath,
      trigger: 'oauth_return',
    })
    void analyzeImage(resumeFlow.mode)
  }, [
    user,
    quotaResumePending,
    uploadedImageUrl,
    zipCode,
    isZipCodeValid,
    isAnalyzing,
    analysisResult,
    diagnosisResult,
    analyzeImage,
    skipZipStep,
  ])

  // Reset upload handler
  const resetUpload = useCallback(() => {
    autoStartPhotoRef.current = null
    const previousAttemptId = attemptIdRef.current
    if (previousAttemptId) {
      pendingRetryRef.current = {
        previousAttemptId,
        fromResult: !!(analysisResult || diagnosisResult),
        mode: analysisMode,
      }
    }

    setUploadedImage(null)
    setUploadedImageUrl(null)
    setUploadedImageFile(null)
    setSupportingImages([])
    setSupportingImageError("")
    setIsUploadingSupportingImages(false)
    setIsRefiningWithSupportingPhoto(false)
    setSupportingPhotoGuidance(null)
    setPhotoQuality(null)
    setSupportingPhotoQuality({})
    photoQualityTokenRef.current = null
    supportingQualityUrlsRef.current.clear()
    setAnalysisResult(null)
    setDiagnosisResult(null)
    setRecoveredWeed(null)
    setIsRecoveringWeed(false)
    setWeedRecoveryFailed(false)
    setIsAnalyzing(false)
    setLimitReached(false)
    setScanUsage(null)
    setUpgradeRequired(false)
    setQuotaResumeId(null)
    setQuotaResumePending(false)
    analysisResumeFlowRef.current = null
    setAnalysisNetworkError(false)
    setAnalysisSaveError(false)
    setAnalysisServerError(false)
    setEmailGateCompleted(false)
    setCompletedTimelineUrl(null)
    setCarePlanEmail("")
    setEmailSuggestion(null)
    setSavedAnalysisId(null)
    setResultAccessToken(null)
    setPhotoCapGate(null)
    setRefinementPolicy(null)
    setRefinementSummary(null)
    refinementTriggerRef.current = null
    previousOutcomeRef.current = null
    funnelIdRef.current = null
    primaryImageMetricsRef.current = {}
    attemptIdRef.current = null
    resultViewedFiredForRef.current = null
    analyzeInFlightRef.current = false
    carePlanNavigatedRef.current = false
    if (carePlanRedirectTimerRef.current) {
      clearTimeout(carePlanRedirectTimerRef.current)
      carePlanRedirectTimerRef.current = null
    }
    try {
      localStorage.removeItem('pendingAnalysis_limit')
      localStorage.removeItem('pendingAnalysis_diagnosis')
      localStorage.removeItem('pendingAnalysis_flow')
      sessionStorage.removeItem('core_analysis_funnel_id')
    } catch { /* noop */ }
    // Clear the back-button rescue breadcrumb — a new analysis is starting,
    // and once it completes the savedAnalysisId effect will write a fresh
    // breadcrumb keyed to the new result.
    clearBreadcrumb()
  }, [analysisResult, diagnosisResult, analysisMode])

  const retryWithNewPhoto = useCallback(() => {
    const analysisIdToLink = savedAnalysisId
    const funnelIdToKeep = funnelIdRef.current
    resetUpload()
    rescanOfAnalysisIdRef.current = analysisIdToLink
    // A different photo is a new attempt inside the same user journey, not a
    // brand-new funnel. resetUpload intentionally clears the funnel for true
    // exits, so restore it only through this explicit retry path.
    funnelIdRef.current = funnelIdToKeep
    if (funnelIdToKeep) {
      try {
        sessionStorage.setItem('core_analysis_funnel_id', funnelIdToKeep)
      } catch { /* noop */ }
    }
  }, [resetUpload, savedAnalysisId])

  // Accuracy recovery for an inconclusive result. Keep the primary photo and
  // ZIP, then ask for one targeted supporting view. The user still presses the
  // final analyze button, so this never spends quota merely by opening the
  // refinement step. Covers identify and diagnose; weed keeps its own recovery
  // machinery and falls back to a plain new-photo retry.
  const refineWithSupportingPhoto = useCallback((triggerArg?: RefinementTrigger | unknown) => {
    const isDiagnoseRefinement = analysisMode === 'diagnose'
    if (!uploadedImageUrl || (analysisMode !== 'identify' && !isDiagnoseRefinement)) {
      retryWithNewPhoto()
      return
    }
    const refinementMode: 'identify' | 'diagnose' = isDiagnoseRefinement ? 'diagnose' : 'identify'

    const previousAttemptId = attemptIdRef.current
    const rootAnalysisId = savedAnalysisId

    // Identify carries confidence/gating at the top level; diagnose nests the
    // equivalents in its optional quality envelope (older rows omit it).
    const diagnosisQuality = diagnosisResult?.quality ?? null
    const displayConfidence = isDiagnoseRefinement
      ? diagnosisQuality?.displayConfidence ?? diagnosisResult?.confidence ?? null
      : analysisResult?.displayConfidence ?? analysisResult?.confidence ?? null
    const gatingReason = isDiagnoseRefinement
      ? diagnosisQuality?.abstentionReason ?? null
      : analysisResult?.abstentionReason ?? null
    // Captured for BOTH lanes: the anonymous gate below sends the visitor
    // through OAuth, and oauthResumeUrl carries this outcome across so the
    // delta the refinement produces is still attributable on the return.
    const previousOutcome = {
      outcome: isDiagnoseRefinement
        ? diagnoseOutcomeLabel(diagnosisResult)
        : identifyOutcomeLabel(analysisResult),
      confidence: displayConfidence,
    }

    // These CTAs are also wired straight to onClick, so the first argument can
    // be a MouseEvent. Only accept a value from the known union.
    const trigger: RefinementTrigger = isRefinementTrigger(triggerArg)
      ? triggerArg
      : refinementPolicy?.reason
        ?? (gatingReason === 'poor_image'
          ? 'poor_image'
          : gatingReason === 'insufficient_visual_evidence'
            ? 'inconclusive'
            : isDiagnoseRefinement ? 'inconclusive' : 'low_margin')

    // The primary enticement moment. An anonymous visitor sees the CTA on
    // every refinable result, and the tap is what asks for the account. The
    // photo and ZIP stay in state, so signing in resumes exactly here.
    if (!user) {
      // Impression already counted by the render effect; the tap emits only
      // refinement_started so the gate denominator stays one-per-view.
      trackEvent('refinement_started', {
        ...analysisFunnelEnvelope({
          funnelId: funnelIdRef.current,
          attemptId: previousAttemptId,
          analysisId: rootAnalysisId,
          selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
          mode: refinementMode,
          source: isDiagnoseRefinement ? 'diagnose_result_refinement' : 'identify_result_refinement',
          authState: 'anonymous',
          actualDestination: 'refinement_signin_gate',
        }),
        mode: refinementMode,
        trigger,
        auth_state: 'anonymous',
        image_count: 1 + supportingImages.length,
        chain_length: refinementPolicy?.chain?.length ?? 0,
        outcome: 'gated',
        platform: 'web',
      })
      refinementTriggerRef.current = trigger
      previousOutcomeRef.current = previousOutcome
      setPhotoCapGate({
        intent: 'refinement',
        requiredTier: 'registered_free',
        mode: refinementMode,
        cap: photoCap,
        requested: 1 + supportingImages.length + 1,
        reason: trigger,
      })
      return
    }

    // A free user whose one exempt refinement is spent would fall through to
    // normal monthly quota server-side. Route the tap to the premium wall
    // instead of silently consuming an allowance.
    if (refinementPolicy?.requiresUpgrade && refinementPolicy.refinementsRemaining === 0) {
      // Impression already counted by the render effect (same rule as the
      // anonymous guard above): the tap emits only refinement_started.
      trackEvent('refinement_started', {
        ...analysisFunnelEnvelope({
          funnelId: funnelIdRef.current,
          attemptId: previousAttemptId,
          analysisId: rootAnalysisId,
          selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
          mode: refinementMode,
          source: isDiagnoseRefinement ? 'diagnose_result_refinement' : 'identify_result_refinement',
          authState: 'authenticated',
          actualDestination: 'refinement_upgrade_gate',
        }),
        mode: refinementMode,
        trigger,
        auth_state: 'authenticated',
        image_count: 1 + supportingImages.length,
        chain_length: refinementPolicy?.chain?.length ?? 0,
        outcome: 'gated',
        platform: 'web',
      })
      setPhotoCapGate({
        intent: 'refinement',
        requiredTier: 'premium',
        mode: refinementMode,
        cap: photoCap,
        requested: 1 + supportingImages.length + 1,
        reason: trigger,
      })
      return
    }

    if (rootAnalysisId) rescanOfAnalysisIdRef.current = rootAnalysisId
    refinementTriggerRef.current = trigger
    previousOutcomeRef.current = previousOutcome

    trackEvent('refinement_started', {
      ...analysisFunnelEnvelope({
        funnelId: funnelIdRef.current,
        attemptId: previousAttemptId,
        analysisId: rootAnalysisId,
        selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
        mode: refinementMode,
        source: isDiagnoseRefinement ? 'diagnose_result_refinement' : 'identify_result_refinement',
        authState: 'authenticated',
        actualDestination: 'supporting_photo_capture',
      }),
      mode: refinementMode,
      trigger,
      auth_state: 'authenticated',
      image_count: 1 + supportingImages.length,
      chain_length: refinementPolicy?.chain?.length ?? 0,
      outcome: 'opened',
      platform: 'web',
    })

    analytics.grassIdRetryStarted({
      previousAttemptId,
      fromResult: true,
      mode: refinementMode,
      funnelId: funnelIdRef.current ?? undefined,
      selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
      authState: user ? 'authenticated' : 'anonymous',
    })

    // A refinement is "the photos already read, plus one new targeted view" —
    // discarding the supporting set would throw away evidence the model has
    // already been paid to read, and would cap every refinement at two photos
    // however high the tier cap is. Trim only when the set is already at the
    // cap, dropping the oldest supporting view so the new one has a slot.
    const trimToRefinementSlots = (current: SupportingAnalysisImage[]) => {
      const slots = Math.max(0, photoCap - 1)
      if (current.length < slots) return current
      return current.slice(current.length - Math.max(0, slots - 1))
    }
    setSupportingImages(trimToRefinementSlots)

    // The primary capture warning belongs to the photo that already ran. Left
    // up, it renders directly above the "add a photo" panel and reads as a
    // verdict on the photo the user is about to take, and its only action
    // (retryWithNewPhoto -> resetUpload) would silently discard the refinement
    // they just started. Supporting warnings survive with their thumbnails, so
    // only prune the keys whose views the trim above dropped.
    setPhotoQuality(null)
    photoQualityTokenRef.current = null
    const keptUrls = new Set(trimToRefinementSlots(supportingImages).map((image) => image.url))
    // Pending measurements for trimmed views must not land either.
    supportingQualityUrlsRef.current = keptUrls
    setSupportingPhotoQuality((prev) => {
      const keys = Object.keys(prev)
      if (keys.length === 0) return prev
      const next: Record<string, PhotoQualityReason> = {}
      for (const key of keys) {
        if (keptUrls.has(key)) next[key] = prev[key]
      }
      return Object.keys(next).length === keys.length ? prev : next
    })
    setSupportingImageError("")
    setAnalysisResult(null)
    setDiagnosisResult(null)
    setSavedAnalysisId(null)
    setLimitReached(false)
    setUpgradeRequired(false)
    setAnalysisNetworkError(false)
    setAnalysisSaveError(false)
    setAnalysisServerError(false)
    // The ask itself now lives in lib/analysis/refinement-policy so the server
    // policy and this client fallback cannot drift apart.
    setSupportingPhotoGuidance(
      refinementPolicy?.nextPhotoGuidance
      ?? resolveNextPhotoGuidance(
        refinementMode,
        isDiagnoseRefinement ? diagnosisResult : analysisResult,
      ),
    )
    setRefinementSummary(null)
    setIsRefiningWithSupportingPhoto(true)
    attemptIdRef.current = newAttemptId()
    resultViewedFiredForRef.current = null
    analyzeInFlightRef.current = false
  }, [
    analysisMode,
    analysisResult,
    diagnosisResult,
    newAttemptId,
    retryWithNewPhoto,
    savedAnalysisId,
    uploadedImageUrl,
    user,
    photoCap,
    refinementPolicy,
    supportingImages,
  ])

  // Impression for every refinement offer that renders, signed in or locked,
  // including the default additive retry ('retry_additive'). This is the
  // denominator the whole funnel is measured against, so it fires once per
  // result rather than once per mount, and the trigger keeps the lanes apart.
  const refinementGateShownForRef = useRef<string | null>(null)
  const handleRefinementGateShown = useCallback((reason: RefinementTrigger) => {
    const key = `${savedAnalysisId ?? attemptIdRef.current ?? 'pending'}:${reason}`
    if (refinementGateShownForRef.current === key) return
    refinementGateShownForRef.current = key
    trackEvent('refinement_gate_shown', {
      ...analysisFunnelEnvelope({
        funnelId: funnelIdRef.current,
        attemptId: attemptIdRef.current,
        analysisId: savedAnalysisId ?? rescanOfAnalysisIdRef.current,
        selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
        mode: analysisMode,
        source: 'result_refinement_offer',
        authState: user ? 'authenticated' : 'anonymous',
        actualDestination: user ? 'supporting_photo_capture' : 'refinement_signin_gate',
      }),
      mode: analysisMode,
      trigger: reason,
      auth_state: user ? 'authenticated' : 'anonymous',
      image_count: 1 + supportingImages.length,
      photo_cap: photoCap,
      chain_length: refinementPolicy?.chain?.length ?? 0,
      locked: !user,
      platform: 'web',
    })
  }, [analysisMode, photoCap, refinementPolicy, savedAnalysisId, supportingImages.length, user])

  const dismissPhotoCapGate = useCallback(() => setPhotoCapGate(null), [])

  // The locked supporting slot inside the refinement panel — the lane a saved
  // result hops into. It is the same ask as the post-result CTA, so it opens
  // the same gate: the root lineage below is what keeps the promised free
  // re-run off the monthly allowance after OAuth.
  const requestSupportingPhotoSignIn = useCallback(() => {
    const trigger: RefinementTrigger =
      refinementTriggerRef.current ?? refinementPolicy?.reason ?? 'retry_additive'
    // Same capture the post-result CTAs do. Without it this lane -- the saved
    // result's hop into the refinement panel -- crossed OAuth with no previous
    // outcome, so refinement_outcome_delta silently skipped even when the photo
    // was added. Never overwrite an outcome the resume already rehydrated.
    if (!previousOutcomeRef.current) {
      const source = diagnosisResult ?? options?.initialSavedAnalysis?.diagnosis ?? null
      const identify = analysisResult ?? options?.initialSavedAnalysis?.result ?? null
      if (source) {
        previousOutcomeRef.current = {
          outcome: diagnoseOutcomeLabel(source),
          confidence: source.quality?.displayConfidence ?? source.confidence ?? null,
        }
      } else if (identify) {
        previousOutcomeRef.current = {
          outcome: identifyOutcomeLabel(identify),
          confidence: identify.displayConfidence ?? identify.confidence ?? null,
        }
      }
    }
    trackEvent('refinement_gate_shown', {
      ...analysisFunnelEnvelope({
        funnelId: funnelIdRef.current,
        attemptId: attemptIdRef.current,
        analysisId: rescanOfAnalysisIdRef.current ?? savedAnalysisId,
        selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
        mode: analysisMode,
        source: 'refinement_photo_slot',
        authState: 'anonymous',
        actualDestination: 'refinement_signin_gate',
      }),
      mode: analysisMode,
      trigger,
      auth_state: 'anonymous',
      image_count: 1 + supportingImages.length,
      photo_cap: photoCap,
      locked: true,
      surface: 'refinement_slot',
      platform: 'web',
    })
    setPhotoCapGate({
      intent: 'refinement',
      requiredTier: 'registered_free',
      mode: analysisMode,
      cap: photoCap,
      requested: 2 + supportingImages.length,
      reason: trigger,
    })
  }, [
    analysisMode,
    analysisResult,
    diagnosisResult,
    options?.initialSavedAnalysis,
    photoCap,
    refinementPolicy,
    savedAnalysisId,
    supportingImages.length,
  ])

  // Re-run the analysis after a retryable network or save failure without
  // making the user select the photo again. The uploaded image URL and ZIP are
  // still in state, so analyzeImage sends the request again. A save retry gets
  // a new attempt ID because the failed attempt's anonymous slot was refunded.
  const retryAnalysis = useCallback(() => {
    const previousAttemptId = attemptIdRef.current
    setAnalysisNetworkError(false)
    setAnalysisSaveError(false)
    setAnalysisServerError(false)
    analytics.grassIdRetryStarted({
      previousAttemptId,
      fromResult: false,
      mode: analysisMode,
      funnelId: funnelIdRef.current ?? undefined,
      selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
      authState: user ? 'authenticated' : 'anonymous',
    })
    void analyzeImage()
  }, [analyzeImage, analysisMode, user])

  // Fire visibility-based analytics events when post-result DOM lands:
  //   - `grass_id_result_viewed` on #results-section (legacy, all variants)
  //   - `result_plan_preview_scrolled` on #care-plan-preview (hybrid layout)
  //   - `result_upgrade_prompted` on #care-plan-upgrade (hybrid layout)
  // Each observer fires on `entry.isIntersecting` (no ratio gate — see
  // commit history for the IO measurement fix). Each is deduped per attempt
  // id with its own ref. A single MutationObserver waits for the DOM to
  // mount and retries every attach until each one succeeds (the hybrid
  // sections are dynamic-imported, so they appear after a microtask delay).
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!analysisResult && !diagnosisResult) return
    const attemptId = attemptIdRef.current
    if (!attemptId) return

    type ObserverSpec = {
      id: string
      firedRef: { current: string | null }
      fire: () => void
    }

    const grassType = analysisResult?.grassType
      ?? (diagnosisResult ? `diagnosis:${diagnosisResult.problems?.[0]?.name || 'unknown'}` : undefined)
    const confidence = analysisResult?.confidence ?? diagnosisResult?.confidence

    const specs: ObserverSpec[] = [
      {
        id: 'results-section',
        firedRef: resultViewedFiredForRef,
        fire: () => analytics.grassIdResultViewed({
          attemptId,
          mode: analysisMode,
          grassType,
          confidence,
          funnelId: funnelIdRef.current ?? undefined,
          analysisId: savedAnalysisId,
          selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
          source: attemptId.startsWith('saved:') ? 'saved_result' : undefined,
          authState: user ? 'authenticated' : 'anonymous',
          // Saved/restored result views never fired grass_id_result_rendered
          // (no in-session API success), so tag them to keep them out of the
          // bounce-before-scroll delta. Prefixes are set where attemptIdRef is
          // seeded from a saved analysis or a rehydrated session.
          restored: attemptId.startsWith('saved:') || attemptId.startsWith('restored:'),
        }),
      },
      {
        id: 'care-plan-preview',
        firedRef: planPreviewScrolledFiredForRef,
        fire: () => analytics.resultPlanPreviewScrolled({
          attemptId,
          grassType,
        }),
      },
      {
        id: 'result-product-picks-heading',
        firedRef: productsScrolledFiredForRef,
        fire: () => analytics.resultProductsScrolled({
          attemptId,
          grassType,
        }),
      },
      {
        id: 'result-tool-deeplinks-heading',
        firedRef: toolsScrolledFiredForRef,
        fire: () => analytics.resultToolsScrolled({
          attemptId,
          grassType,
        }),
      },
      {
        id: 'care-plan-upgrade',
        firedRef: upgradePromptedFiredForRef,
        fire: () => analytics.resultUpgradePrompted({
          attemptId,
          grassType,
          mode: analysisMode,
          reason: user ? 'premium_required' : 'result_locked',
          surface: `${surfaceFromPathname(pathname)}:care_plan_upgrade`,
        }),
      },
    ]

    // Skip specs that already fired for this attempt — short-circuits work
    // when the effect re-runs (e.g. analysisMode flip).
    const pending = specs.filter((s) => s.firedRef.current !== attemptId)
    if (pending.length === 0) return

    const observers = new Map<string, IntersectionObserver>()
    let mountObserver: MutationObserver | null = null
    let cancelled = false

    const attachOne = (spec: ObserverSpec): boolean => {
      if (cancelled) return true
      if (spec.firedRef.current === attemptId) return true
      if (observers.has(spec.id)) return true
      const el = document.getElementById(spec.id)
      if (!el) return false
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (spec.firedRef.current === attemptId) return
            spec.firedRef.current = attemptId
            spec.fire()
            io.disconnect()
            observers.delete(spec.id)
            return
          }
        }
      })
      io.observe(el)
      observers.set(spec.id, io)
      return true
    }

    const attachAll = (): boolean => {
      let allDone = true
      for (const spec of pending) {
        if (spec.firedRef.current === attemptId) continue
        const attached = attachOne(spec)
        if (!attached) allDone = false
      }
      return allDone
    }

    const raf = requestAnimationFrame(() => {
      if (attachAll()) return

      mountObserver = new MutationObserver(() => {
        if (attachAll()) {
          mountObserver?.disconnect()
          mountObserver = null
        }
      })
      mountObserver.observe(document.body, { childList: true, subtree: true })
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      for (const io of observers.values()) io.disconnect()
      observers.clear()
      mountObserver?.disconnect()
    }
  }, [analysisResult, diagnosisResult, analysisMode, savedAnalysisId, user, pathname])

  // Snapshot of state we want preserved in history.state for popstate-based
  // forward restoration. Updated every render (no dep array) so the URL
  // transition effect below always serializes the latest values without
  // having to subscribe to every piece of state.
  const historyStateRef = useRef<HistoryStatePayload | null>(null)
  historyStateRef.current = {
    savedAnalysisId,
    analysisResult,
    diagnosisResult,
    zipCode,
    uploadedImageUrl,
    supportingImages,
    isUploadingSupportingImages,
    supportingImageError,
    analysisMode,
    resultAccessToken,
  }

  // Surface a saved analysis as /result/<id> in the URL bar so refresh and
  // share-link both work. For the FIRST transition from `/` to a result,
  // pushState — back-button on the result page then returns to a fresh
  // homepage (popstate listener below resets state) and forward restores
  // the result without an SSR round trip. Subsequent in-page changes
  // (re-analyze, swap ID) keep replaceState so history doesn't pile up.
  // Kill switch: `NEXT_PUBLIC_HOMEPAGE_PUSHSTATE_DISABLED=true` falls back
  // to replaceState-only.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const path = window.location.pathname

    if (savedAnalysisId) {
      const target = `/result/${savedAnalysisId}`
      if (path === target) return
      const payload = historyStateRef.current
      if (path === '/' && !PUSHSTATE_DISABLED) {
        // First transition — push so back-button is meaningful.
        window.history.pushState(payload, '', target)
      } else {
        // Already on /result/<X> or pushstate disabled — just rewrite.
        window.history.replaceState(payload, '', target)
      }
    } else if (path.startsWith('/result/')) {
      window.history.replaceState({}, '', '/')
    }
  }, [savedAnalysisId])

  // popstate restoration — handles browser back/forward inside an in-place
  // homepage→result transition. On back to `/`, reset analysis state so the
  // user sees a fresh upload form. On forward to a previously pushState'd
  // `/result/<id>`, restore the analysis state from history.state without an
  // SSR round trip. No-op when pushState is disabled (env kill switch).
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (PUSHSTATE_DISABLED) return

    const onPopState = (event: PopStateEvent) => {
      const path = window.location.pathname

      // Back to a `/result/<id>` we previously serialized — restore from state.
      if (path.startsWith('/result/') && event.state && typeof event.state === 'object') {
        const s = event.state as Partial<HistoryStatePayload>
        if (s.savedAnalysisId) {
          setRecoveredWeed(null)
          setSavedAnalysisId(s.savedAnalysisId)
          if (s.analysisResult) setAnalysisResult(s.analysisResult)
          if (s.diagnosisResult) setDiagnosisResult(s.diagnosisResult)
          if (typeof s.zipCode === 'string') setZipCode(s.zipCode)
          if (typeof s.uploadedImageUrl === 'string') setUploadedImageUrl(s.uploadedImageUrl)
          if (Array.isArray(s.supportingImages)) setSupportingImages(s.supportingImages)
          if (s.analysisMode) setAnalysisMode(s.analysisMode)
          // Capture warnings belong to the upload attempt, not to a restored
          // result; carrying them across would park a stale card on the next
          // upload surface under a restored (`restored:`) attempt id.
          setPhotoQuality(null)
          setSupportingPhotoQuality({})
          photoQualityTokenRef.current = null
          supportingQualityUrlsRef.current.clear()
          if (typeof s.resultAccessToken === 'string') setResultAccessToken(s.resultAccessToken)
          attemptIdRef.current = `restored:${s.savedAnalysisId}`
          // Reset observer dedupe so the restored render re-fires the
          // visibility events under the restored attempt id.
          resultViewedFiredForRef.current = null
          planPreviewScrolledFiredForRef.current = null
          upgradePromptedFiredForRef.current = null
          productsScrolledFiredForRef.current = null
          toolsScrolledFiredForRef.current = null
        }
        return
      }

      // Back to homepage — clear in-place result state so the upload form
      // re-renders. Setters are stable; calling with null is a no-op when
      // nothing was set, so this is safe to call unconditionally.
      if (path === '/') {
        setAnalysisResult(null)
        setDiagnosisResult(null)
        setSavedAnalysisId(null)
        setResultAccessToken(null)
        setRecoveredWeed(null)
        setUploadedImage(null)
        setUploadedImageUrl(null)
        setUploadedImageFile(null)
        setSupportingImages([])
        setSupportingImageError("")
        setPhotoQuality(null)
        setSupportingPhotoQuality({})
        photoQualityTokenRef.current = null
        supportingQualityUrlsRef.current.clear()
        setLimitReached(false)
        setUpgradeRequired(false)
        setEmailGateCompleted(false)
        setCompletedTimelineUrl(null)
        attemptIdRef.current = null
        resultViewedFiredForRef.current = null
        planPreviewScrolledFiredForRef.current = null
        upgradePromptedFiredForRef.current = null
        productsScrolledFiredForRef.current = null
        toolsScrolledFiredForRef.current = null
      }
    }

    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // Write the back-button rescue breadcrumb to localStorage when a saved
  // analysis becomes available. Read by `useLastAnalysisBreadcrumb` on the
  // homepage to surface a "Picked up where you left off" banner when the
  // user lands back on `/` within 60 minutes of a recent analysis.
  // Decoupled from the URL-rewrite effect above so the deps list cleanly
  // tracks the three pieces of data the breadcrumb captures.
  useEffect(() => {
    if (!savedAnalysisId) return
    const grassType = analysisResult?.grassType
      ?? (diagnosisResult
        ? diagnosisResult.outcome === 'healthy'
          ? 'healthy lawn monitoring'
          : diagnosisResult.problems?.[0]?.name || 'diagnosis'
        : null)
    if (!grassType) return
    writeBreadcrumb({
      savedAnalysisId,
      grassType,
      zipCode: zipCode || '',
      savedAt: Date.now(),
    })
  }, [savedAnalysisId, analysisResult?.grassType, diagnosisResult, zipCode])

  const recoverIdentifyAsWeed = useCallback(async (): Promise<boolean> => {
    if (!savedAnalysisId || isRecoveringWeed) return false
    const funnelId = ensureFunnelId()
    const rootAnalysisId = savedAnalysisId
    const attemptId = `weed_recovery_${newAttemptId()}`
    const eventProperties = {
      funnel_id: funnelId,
      root_analysis_id: rootAnalysisId,
      current_analysis_id: rootAnalysisId,
      platform: 'web',
      subject_guess: analysisResult?.subjectGuess,
      result_quality: analysisResult?.quality?.imageQuality ?? analysisResult?.imageQuality,
      model: analysisResult?.model,
      prompt_version: analysisResult?.quality?.promptVersion,
      pipeline_version: analysisResult?.pipelineVersion,
      attempt_id: attemptId,
      weed_entry: 'identify_recovery',
      recovery_kind: 'identify_to_weed',
    }

    setIsRecoveringWeed(true)
    setWeedRecoveryFailed(false)
    trackEvent('identify_to_weed_recovery_started', eventProperties)
    trackEvent('weed_id_scan_started', eventProperties)
    try {
      const response = await fetch('/api/ai/analyze-grass', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'weed',
          recoveryOfAnalysisId: rootAnalysisId,
          sourceResultAccessToken: resultAccessToken || undefined,
          funnelId,
          attemptId,
          distinctId: getPosthogDistinctId(),
        }),
      })
      const data = await safeJson<{
        success?: boolean
        result?: WeedIdentificationResult
        analysisId?: string
        resultAccessToken?: string
        error?: unknown
      }>(response)
      if (!response.ok || !data?.result || !data.analysisId || !data.resultAccessToken) {
        throw new Error(errorMessageFrom(data?.error, 'We could not complete the weed recovery.'))
      }

      setRecoveredWeed({
        result: data.result,
        analysisId: data.analysisId,
        resultAccessToken: data.resultAccessToken,
        attemptId,
      })
      setWeedRecoveryFailed(false)
      trackEvent('identify_to_weed_recovery_completed', {
        ...eventProperties,
        current_analysis_id: data.analysisId,
        weed_id: data.result.weedId || undefined,
        outcome: data.result.outcome,
        usage_exempt: data.result.lineage?.usageExempt === true,
      })
      trackEvent('weed_id_result_rendered', {
        ...eventProperties,
        analysis_id: data.analysisId,
        current_analysis_id: data.analysisId,
        weed_id: data.result.weedId || undefined,
        outcome: data.result.outcome,
        result_confidence: data.result.displayConfidence,
        result_quality: data.result.imageQuality,
        usage_exempt: data.result.lineage?.usageExempt === true,
      })
      const target = `/weed-identifier?analysis_id=${data.analysisId}&access=${encodeURIComponent(data.resultAccessToken)}&funnel_id=${encodeURIComponent(funnelId)}`
      if (typeof window !== 'undefined') {
        // Direct saved-result loads do not already carry our in-memory state in
        // history.state. Seed the source entry before pushing the canonical
        // Weed URL so Back can deterministically restore Identify in place.
        window.history.replaceState(historyStateRef.current, '', window.location.href)
        window.history.pushState({ weedRecovery: true, rootAnalysisId }, '', target)
      }
      return true
    } catch (error) {
      setWeedRecoveryFailed(true)
      trackEvent('identify_to_weed_recovery_failed', {
        ...eventProperties,
        failure_reason: error instanceof Error ? error.message.slice(0, 120) : 'unknown',
      })
      trackEvent('weed_id_scan_failed', {
        ...eventProperties,
        failure_stage: 'analysis',
        failure_reason: error instanceof TypeError ? 'network' : 'request_failed',
      })
      toast.error('Weed ID unavailable', { description: 'Your grass result is still here. Try again, or use Diagnose instead.', position: 'top-right', duration: 5000, closeButton: true })
      return false
    } finally {
      setIsRecoveringWeed(false)
    }
  }, [analysisResult, ensureFunnelId, isRecoveringWeed, newAttemptId, resultAccessToken, savedAnalysisId])

  // Listen for soft-reset events fired by the header when the user clicks
  // the logo / Home link while an analysis is in flight on `/`. The header
  // dispatches `homeReset` instead of doing `window.location.assign('/')`
  // so we don't kill the in-flight fetch and lose the result.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onHomeReset = () => {
      retryWithNewPhoto()
    }
    window.addEventListener('homeReset', onHomeReset)
    return () => {
      window.removeEventListener('homeReset', onHomeReset)
    }
  }, [retryWithNewPhoto])

  // ZIP code validation
  const validateZipCode = useCallback((zip: string) => {
    const result = validateField({
      value: zip,
      rules: VALIDATION_RULES.ZIP_CODE,
      fieldName: "ZIP Code"
    })
    return result.isValid
  }, [])

  // ZIP code change handler
  const handleZipCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const entryMethod = (e as React.ChangeEvent<HTMLInputElement> & {
      entryMethod?: 'typed' | 'saved' | 'geolocation' | 'ip'
    }).entryMethod ?? 'typed'
    const value = e.target.value.replace(/\D/g, '') // Remove non-digits
    setZipCode(value)

    if (value === "") {
      setZipCodeError("")
      setIsZipCodeValid(false)
    } else if (!validateZipCode(value)) {
      const result = validateField({
        value,
        rules: VALIDATION_RULES.ZIP_CODE,
        fieldName: "ZIP Code"
      })
      setZipCodeError(result.errors[0] || "Please enter a valid ZIP code")
      setIsZipCodeValid(false)
    } else {
      setZipCodeError("")
      setIsZipCodeValid(true)
      fireZipValidEntered(value, entryMethod, analysisMode)
    }
  }, [validateZipCode, fireZipValidEntered, analysisMode])

  // ZIP code key press handler
  const handleZipCodeKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isZipCodeValid && zipCode) {
      e.preventDefault()
      analyzeImage()
    }
  }, [isZipCodeValid, zipCode, analyzeImage])

  // Email change handler
  const handleCarePlanEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCarePlanEmail(value)
    setCarePlanEmailError("")

    // Check for email typos when input looks like a complete email
    if (value.includes('@') && value.includes('.')) {
      const suggestion = detectEmailTypo(value)
      setEmailSuggestion(suggestion)
    } else {
      setEmailSuggestion(null)
    }
  }, [])

  // Accept email suggestion handler
  const handleAcceptEmailSuggestion = useCallback(() => {
    if (emailSuggestion) {
      setCarePlanEmail(emailSuggestion.suggested)
      setEmailSuggestion(null)
      // Track that user accepted the suggestion
      trackEvent('email_typo_corrected', {
        original_domain: emailSuggestion.domain,
        corrected_domain: emailSuggestion.correctedDomain
      })
    }
  }, [emailSuggestion])

  // Dismiss email suggestion handler
  const handleDismissEmailSuggestion = useCallback(() => {
    setEmailSuggestion(null)
  }, [])

  const carePlanEventBase = useCallback((
    entryPath: 'anonymous_email_gate' | 'authenticated_result',
    extra: Record<string, string | number | boolean | undefined> = {},
  ) => ({
    attempt_id: attemptIdRef.current ?? undefined,
    analysis_id: savedAnalysisId ?? undefined,
    mode: analysisMode,
    grass_type: analysisResult?.grassType,
    zip_prefix: zipCode ? zipCode.slice(0, 3) : undefined,
    season: getCurrentSeason(),
    entry_path: entryPath,
    user_type: user ? 'authenticated' : 'anonymous',
    ...extra,
  }), [analysisMode, analysisResult?.grassType, savedAnalysisId, user, zipCode])

  // Care plan submit handler (defined before key press handler to avoid stale reference)
  // `location` names the result surface whose submit fired this (button and
  // Enter key pass the same string). Anything but a string is ignored
  // so a handler wired straight to onClick cannot leak an event object.
  const handleCarePlanSubmit = useCallback(async (location?: unknown) => {
    if (carePlanSubmitInFlightRef.current) return

    if (!carePlanEmail.trim()) {
      setCarePlanEmailError("Please enter your email address.")
      toast.error("Email Required", { description: "Please enter your email address to receive your care plan.", position: "top-right", duration: 5000, closeButton: true })
      return
    }

    const submittedEmail = carePlanEmail.trim()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(submittedEmail)) {
      setCarePlanEmailError("Please enter a valid email address.")
      toast.error("Invalid Email", { description: "Please enter a valid email address.", position: "top-right", duration: 5000, closeButton: true })
      return
    }

    setCarePlanEmailError("")
    carePlanSubmitInFlightRef.current = true
    setIsSubmittingCarePlan(true)
    const loadingToastId = toast.loading("Accessing Timeline", { description: "Preparing your personalized lawn care timeline...", position: "top-right", closeButton: true })
    trackEvent('care_plan_cta_clicked', carePlanEventBase('anonymous_email_gate', {
      cta: 'email_submit',
      location: typeof location === 'string' ? location : undefined,
    }))
    trackEvent('care_plan_email_submit_started', carePlanEventBase('anonymous_email_gate'))

    const redirectToTimeline = (
      redirectReason: string,
      emailSaved: boolean,
    ) => {
      // Diagnosis: unlock the treatment plan IN PLACE. The /timeline destination
      // is built from analysisResult.grassType (undefined for a diagnosis) and
      // errors on a missing grassType, so redirecting there would deep-link to a
      // broken page. On a successful capture, reveal the plan via
      // emailGateCompleted (the diagnosis gate reads it) + the on-page success
      // card; on a failed capture, just stay put (no broken redirect).
      if (diagnosisResult) {
        trackEvent('care_plan_redirect_started', carePlanEventBase('anonymous_email_gate', {
          redirect_reason: redirectReason,
          email_saved: emailSaved,
          diagnosis_inline_unlock: true,
        }))
        if (emailSaved) {
          carePlanNavigatedRef.current = true // block any navigation
          setEmailGateCompleted(true)
        }
        return
      }

      const timelineUrl = buildTimelineUrl({
        zipCode,
        grassType: analysisResult?.grassType,
        savedAnalysisId,
        lawnId: analysisLawnContextRef.current.lawnId,
        uploadedImageUrl,
        email: submittedEmail,
      })
      trackEvent('care_plan_redirect_started', carePlanEventBase('anonymous_email_gate', {
        redirect_reason: redirectReason,
        email_saved: emailSaved,
      }))

      // Failed capture (save/network error): we still grant anonymous timeline
      // access, but must NOT show the "your plan is on the way" success card —
      // the email did not save, so the celebratory frame would be a lie. Go
      // straight to the timeline, exactly as before the success-frame change.
      if (!emailSaved) {
        router.push(timelineUrl)
        return
      }

      // Successful capture only: make the email-capture moment the peak of the
      // flow — render the on-page success card first (growth-design rule 1),
      // then auto-advance to the timeline after a short beat. The success card's
      // "See my care plan" button calls navigateToCompletedTimeline() to pre-empt
      // the beat for users who'd rather not wait. carePlanNavigatedRef guards
      // against the button and the timer both firing router.push.
      carePlanNavigatedRef.current = false
      setCompletedTimelineUrl(timelineUrl)
      setEmailGateCompleted(true)

      const navigate = () => {
        if (carePlanNavigatedRef.current) return
        carePlanNavigatedRef.current = true
        if (carePlanRedirectTimerRef.current) {
          clearTimeout(carePlanRedirectTimerRef.current)
          carePlanRedirectTimerRef.current = null
        }
        router.push(timelineUrl)
      }
      // When the anonymous identify success card can offer OAuth, hold on that
      // peak instead of auto-advancing away from the account-save ask.
      if (timelineUrl && !user) return
      // Long enough for the grass-specific summary + social-proof line to land
      // as the peak before auto-advancing; the success card's button lets
      // impatient users skip the beat.
      carePlanRedirectTimerRef.current = setTimeout(navigate, 3200)
    }

    try {
      // Diagnosis captures send the diagnosed problems + treatments so the
      // email carries what the user actually came for (not the generic grass
      // care calendar), plus the saved-analysis id so the email can link back
      // to /result/<id>. Identify captures keep the care-plan payload.
      const requestBody = diagnosisResult
        ? {
            email: submittedEmail,
            context: 'diagnosis',
            zipCode,
            resultId: savedAnalysisId ?? undefined,
            diagnosis: {
              problems: diagnosisResult.problems,
              treatmentRecommendations: diagnosisResult.treatmentRecommendations,
              overallAssessment: diagnosisResult.overallAssessment,
              immediateActions: diagnosisResult.immediateActions,
              region: diagnosisResult.region,
            },
          }
        : {
            email: submittedEmail,
            context: 'care_plan',
            grassType: analysisResult?.grassType,
            zipCode,
          }

      const response = await fetch('/api/email/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (response.ok) {
        const okData = await response.json().catch(() => ({}))
        if (loadingToastId) toast.dismiss(loadingToastId)
        // Be honest: only promise the inbox when the send actually succeeded.
        // On a send failure the user still lands on their plan via the redirect.
        if (okData.emailSent === false) {
          toast.success("Saved!", { description: "We had trouble emailing your plan, but here it is.", position: "top-right", duration: 5000, closeButton: true })
        } else {
          toast.success("Plan sent!", { description: "Check your inbox for your care timeline.", position: "top-right", duration: 5000, closeButton: true })
        }

        // Track email capture — diagnosis captures use their own source so the
        // diagnosis funnel separates from identify in analytics.
        analytics.emailCaptured(
          diagnosisResult ? 'diagnosis_gate' : 'grass_id_gate',
          analysisResult?.grassType,
          {
            attemptId: attemptIdRef.current ?? undefined,
            analysisId: savedAnalysisId ?? undefined,
            funnelId: funnelIdRef.current ?? undefined,
          },
        )

        // Redirect directly to timeline
        redirectToTimeline('email_captured', true)
      } else {
        const errorData = await response.json().catch(() => ({}))
        if (loadingToastId) toast.dismiss(loadingToastId)

        if (errorData.error === 'already_subscribed') {
          toast.success("Welcome back!", {
            description: errorData.emailSent === false
              ? "We had trouble emailing your plan, but here it is."
              : "Check your inbox for your care timeline.",
            position: "top-right",
            duration: 5000,
            closeButton: true,
          })

          // Track returning subscriber
          analytics.emailCaptured(
            diagnosisResult ? 'diagnosis_gate_returning' : 'grass_id_gate_returning',
            analysisResult?.grassType,
            {
              attemptId: attemptIdRef.current ?? undefined,
              analysisId: savedAnalysisId ?? undefined,
              funnelId: funnelIdRef.current ?? undefined,
            },
          )

          // Redirect for returning subscribers too
          redirectToTimeline('already_subscribed', true)
        } else {
          // Even on error, redirect them
          toast.error("Note", { description: "We couldn't save your email, but here are your results!", position: "top-right", duration: 5000, closeButton: true })
          trackEvent('care_plan_email_submit_failed', carePlanEventBase('anonymous_email_gate', {
            status: response.status,
            error: typeof errorData.error === 'string' ? errorData.error : 'unknown',
          }))
          redirectToTimeline('email_save_failed', false)
        }
      }
    } catch (error) {
      if (loadingToastId) toast.dismiss(loadingToastId)
      // Even on network error, redirect them
      toast.error("Note", { description: "We couldn't save your email, but here are your results!", position: "top-right", duration: 5000, closeButton: true })
      trackEvent('care_plan_email_submit_failed', carePlanEventBase('anonymous_email_gate', {
        error: error instanceof Error ? error.message : 'network_error',
      }))
      redirectToTimeline('email_network_failed', false)
    } finally {
      carePlanSubmitInFlightRef.current = false
      setIsSubmittingCarePlan(false)
    }
  }, [carePlanEmail, analysisResult, diagnosisResult, zipCode, uploadedImageUrl, savedAnalysisId, user, router, carePlanEventBase])

  // Pre-empt the post-capture auto-advance beat. Wired to the success card's
  // "See my care plan" link so an impatient user lands on the timeline
  // immediately. Navigation itself stays native in the rendering component;
  // this callback only clears the fallback timer and records the click.
  const navigateToCompletedTimeline = useCallback(() => {
    if (carePlanNavigatedRef.current) return
    if (!completedTimelineUrl) return
    carePlanNavigatedRef.current = true
    if (carePlanRedirectTimerRef.current) {
      clearTimeout(carePlanRedirectTimerRef.current)
      carePlanRedirectTimerRef.current = null
    }
    trackEvent('care_plan_success_cta_clicked', carePlanEventBase('anonymous_email_gate', {
      cta: 'see_my_care_plan',
    }))
  }, [completedTimelineUrl, carePlanEventBase])

  // Clear any pending auto-advance timer on unmount so a navigation that lands
  // elsewhere can't fire a late router.push.
  useEffect(() => {
    return () => {
      if (carePlanRedirectTimerRef.current) {
        clearTimeout(carePlanRedirectTimerRef.current)
        carePlanRedirectTimerRef.current = null
      }
    }
  }, [])

  // Fired by the upgrade card when the user takes its conversion action —
  // OAuth click (before redirect) or email submit. The click step between
  // result_upgrade_prompted and email_captured/signup_completed.
  const handleResultPrimaryCtaClick = useCallback((method: 'oauth' | 'email') => {
    // On the diagnose flow `analysisResult` is null, so grassType/confidence
    // would be undefined — carry the diagnosed problem + its confidence instead
    // so the CTA-click event keeps a subject dimension (mirrors the
    // result_upgrade_prompted event's diagnosis handling).
    const problem = diagnosisResult?.problems?.[0]?.name
    analytics.resultPrimaryCtaClicked({
      attemptId: attemptIdRef.current ?? 'no-attempt',
      grassType: analysisResult?.grassType,
      problem,
      method,
      confidence: analysisResult?.confidence ?? diagnosisResult?.confidence,
      entryPath: diagnosisResult ? 'diagnose' : 'homepage',
      analysisId: savedAnalysisId ?? undefined,
      mode: diagnosisResult ? 'diagnose' : 'identify',
      planType: diagnosisResult?.outcome === 'healthy'
        ? 'monitoring'
        : diagnosisResult ? 'treatment' : 'care',
      outcome: diagnosisResult?.outcome || (analysisResult ? 'identified' : undefined),
    })
  }, [analysisResult, diagnosisResult, savedAnalysisId])

  // Quota-warning banner instrumentation (anon result with 1 scan remaining).
  const handleScanQuotaWarningShown = useCallback(() => {
    // The banner only renders at remaining <= 0, so a flat 'quota_low' label
    // reported an exhausted allowance as a warning. Derive it from the value
    // the banner actually gated on.
    const remaining = scanUsage?.remaining ?? 1
    const reason: AnalysisGateReason =
      remaining > 0
        ? 'quota_low'
        : user
          ? 'registered_quota_exhausted'
          : 'anon_quota_exhausted'
    analytics.scanQuotaWarningShown({
      attemptId: attemptIdRef.current ?? 'no-attempt',
      remaining,
      grassType: analysisResult?.grassType,
      mode: analysisMode,
      reason,
      surface: `${surfaceFromPathname(pathname)}:result_quota_banner`,
    })
  }, [scanUsage, analysisResult, analysisMode, pathname, user])

  const handleScanQuotaWarningClicked = useCallback(() => {
    analytics.scanQuotaWarningClicked({
      attemptId: attemptIdRef.current ?? 'no-attempt',
      remaining: scanUsage?.remaining ?? 1,
      grassType: analysisResult?.grassType,
    })
  }, [scanUsage, analysisResult])

  // Impermanence-note click ("this result isn't saved to an account").
  const handleImpermanenceNoteClick = useCallback(() => {
    trackEvent('result_impermanence_note_clicked', {
      attempt_id: attemptIdRef.current ?? 'no-attempt',
      grass_type: analysisResult?.grassType,
    })
  }, [analysisResult])

  // Email key press handler (defined after submit handler so it can reference it)
  // Hosts pass the same `location` their submit button sends.
  const handleCarePlanEmailKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>, location?: string) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCarePlanSubmit(location)
    }
  }, [handleCarePlanSubmit])

  // Save and view timeline handler for authenticated users
  const handleSaveAndViewTimeline = useCallback(async (location?: unknown) => {
    if (!user || !analysisResult) return

    setIsSubmittingCarePlan(true)
    const loadingToastId = toast.loading("Saving Analysis", { description: "Saving to your dashboard...", position: "top-right", closeButton: true })
    trackEvent('care_plan_cta_clicked', carePlanEventBase('authenticated_result', {
      cta: savedAnalysisId ? 'view_timeline' : 'save_and_view_timeline',
      location: typeof location === 'string' ? location : undefined,
    }))
    trackEvent('care_plan_save_started', carePlanEventBase('authenticated_result', {
      already_saved: Boolean(savedAnalysisId),
    }))

    let resolvedAnalysisId = savedAnalysisId
    let saveFailed = false

    try {
      // Only save if not already persisted (analyzeImage already saves for auth'd users)
      if (!savedAnalysisId) {
        const saveResponse = await fetch('/api/user/save-analysis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grassType: analysisResult.grassType,
            confidence: analysisResult.confidence,
            zipCode: zipCode,
            imageUrl: uploadedImageUrl,
            analysisResult: analysisResult
          })
        })

        if (!saveResponse.ok) {
          throw new Error('Failed to save analysis')
        }

        // Capture analysis ID for potential lawn creation
        const saveData = await saveResponse.json()
        resolvedAnalysisId = typeof saveData.analysisId === 'string' ? saveData.analysisId : savedAnalysisId
        if (saveData.analysisId) {
          setSavedAnalysisId(saveData.analysisId)
        }
      }

      // Clear pending analysis from localStorage
      try {
        localStorage.removeItem('pendingAnalysis')
      } catch {
        // localStorage can be unavailable in private browsing modes.
      }

      if (loadingToastId) toast.dismiss(loadingToastId)
      toast.success("Analysis Saved!", { description: "Redirecting to your care timeline...", position: "top-right", duration: 5000, closeButton: true })
    } catch (error) {
      saveFailed = true
      if (loadingToastId) toast.dismiss(loadingToastId)
      toast.error("Save Skipped", { description: "We couldn't save this analysis, but your care timeline is loading.", position: "top-right", duration: 5000, closeButton: true })
      trackEvent('care_plan_save_failed', carePlanEventBase('authenticated_result', {
        error: error instanceof Error ? error.message : 'unknown',
      }))
      console.error('Failed to save analysis:', error)
    } finally {
      setIsSubmittingCarePlan(false)
    }

    const timelineUrl = buildTimelineUrl({
      zipCode,
      grassType: analysisResult.grassType,
      savedAnalysisId: resolvedAnalysisId,
      lawnId: analysisLawnContextRef.current.lawnId,
      uploadedImageUrl,
    })
    trackEvent('care_plan_redirect_started', carePlanEventBase('authenticated_result', {
      redirect_reason: saveFailed ? 'save_failed' : 'analysis_saved',
      save_failed: saveFailed,
      analysis_id: resolvedAnalysisId ?? undefined,
    }))
    router.push(timelineUrl)
  }, [user, analysisResult, zipCode, uploadedImageUrl, savedAnalysisId, router, carePlanEventBase])

  // Build the saved-plan destination for OAuth. Grass identification opens the
  // 12-month timeline. Diagnosis opens its durable /result page because the
  // grass timeline cannot render diagnosis treatment data.
  const timelineRedirectUrl = (() => {
    if (diagnosisResult && savedAnalysisId) return `/result/${savedAnalysisId}`
    if (!analysisResult?.grassType || !zipCode) return undefined
    return buildTimelineUrl({
      zipCode,
      grassType: analysisResult.grassType,
      savedAnalysisId,
      resultAccessToken,
      lawnId: analysisLawnContextRef.current.lawnId,
      uploadedImageUrl,
    })
  })()

  // Build a homepage URL that includes analysis state as params (survives OAuth on all platforms)
  const oauthResumeUrl = (() => {
    // Callers can override the post-OAuth landing path. The saved-result
    // page passes `/result/<id>` so the user returns to their already-
    // persisted analysis after sign-in instead of the homepage. No need
    // to pack analysis state into query params there — the DB row is the
    // source of truth.
    if (options?.oauthResumePath) return options.oauthResumePath

    // A refinement gate outranks a stale quota-resume id: the visitor is being
    // sent to sign in for THIS result, and the refinement lineage below is what
    // keeps the promised free re-run off their monthly allowance.
    // A saved-result hop carries its root only in the rescan ref (the homepage
    // never persisted a row of its own), while a live post-result refinement
    // has it in state. Either one is the lineage the resume must preserve.
    // A 'photo_cap' denial mid-refinement (the server refused the photo set
    // because the visitor is signed out) is the SAME refinement: the visitor
    // already picked the extra view. Dropping the lineage there sent them back
    // to a bare ZIP step and billed the re-run.
    // A denial only counts as a refinement when the refinement lane itself set
    // the rescan ref: `savedAnalysisId` and `resultAccessToken` outlive a new
    // primary upload, so reading them here would pin a fresh scan to the
    // previous root and put that root's token in the OAuth URL.
    const refinementRootId = photoCapGate?.intent === 'refinement'
      ? savedAnalysisId ?? rescanOfAnalysisIdRef.current
      : rescanOfAnalysisIdRef.current
    // The tap intent, independent of whether a lineage exists to prove it. An
    // anonymous result that never persisted has no root id and no token, but
    // the visitor still tapped a refinement CTA: resuming them as a quota re-run
    // spends a scan on a single-photo repeat of the answer they already have.
    const refinementIntent =
      photoCapGate?.intent === 'refinement' || photoCapGate?.intent === 'photo_cap'
    const refinementResume =
      refinementIntent &&
      Boolean(refinementRootId) &&
      Boolean(resultAccessToken)

    if (quotaResumeId && !refinementIntent) {
      const params = new URLSearchParams({
        resumeAnalysis: '1',
        analysisResumeId: quotaResumeId,
        mode: analysisMode,
      })
      // The full first-party redirect retains the photo fields. AuthContext
      // deliberately removes them from the provider-facing callback fallback,
      // which still keeps the same tool + resume id if browser storage fails.
      if (uploadedImageUrl && isTrustedAnalysisImageUrl(uploadedImageUrl)) {
        params.set('resume', '1')
        params.set('imageUrl', uploadedImageUrl)
        supportingImages.forEach((image) => params.append('supportingImageUrl', image.url))
        if (zipCode) params.set('zipCode', zipCode)
      }
      const resumePath = pathname === '/diagnose' ? '/diagnose' : '/'
      return `${resumePath}?${params.toString()}`
    }

    if (!uploadedImageUrl || !isTrustedAnalysisImageUrl(uploadedImageUrl)) {
      if (typeof window === 'undefined') return '/'
      return `${window.location.pathname}${window.location.search}${window.location.hash}`
    }
    const params = new URLSearchParams({ resume: '1' })
    params.set('imageUrl', uploadedImageUrl)
    supportingImages.forEach((image) => params.append('supportingImageUrl', image.url))
    if (zipCode) params.set('zipCode', zipCode)
    if (analysisMode !== 'identify') params.set('mode', analysisMode)
    // A signed-out refinement tap sends the visitor through OAuth with the
    // result still on screen. Carry the root analysis id and its access token
    // so the run they come back to is still a refinement of that row: without
    // both, the server cannot prove ownership and the free re-run the gate
    // promised silently spends a monthly scan.
    if (refinementResume && refinementRootId && resultAccessToken) {
      params.set('rescanOfAnalysisId', refinementRootId)
      params.set('sourceResultAccessToken', resultAccessToken)
    }
    // The refinement SHAPE rides the intent, not the lineage. Without these the
    // restore below has no way to tell a refinement return from a plain photo
    // resume, and the visitor lands on a bare ZIP step having signed in for a
    // second photo they are never offered. With no lineage the re-run is a
    // fresh billed analysis; the free-re-run copy is gated on a live policy, so
    // nothing promises otherwise in that state.
    if (refinementIntent) {
      const trigger = photoCapGate?.reason ?? refinementTriggerRef.current
      if (trigger) params.set('refinementTrigger', trigger)
      const previousOutcome = previousOutcomeRef.current
      if (previousOutcome) {
        params.set('refinementPrevOutcome', previousOutcome.outcome)
        if (typeof previousOutcome.confidence === 'number') {
          params.set('refinementPrevConfidence', String(Math.round(previousOutcome.confidence)))
        }
      }
    }
    const resumePath = pathname === '/diagnose' ? '/diagnose' : '/'
    return `${resumePath}?${params.toString()}`
  })()

  const recoveryRedirectUrl = options?.oauthResumePath
    ? `${options.oauthResumePath}${resultAccessToken ? `?access=${encodeURIComponent(resultAccessToken)}` : ''}`
    : (recoveryId
    ? `${pathname === '/diagnose' ? '/diagnose' : '/'}?scanRecovery=${recoveryId}&mode=${analysisMode}`
    : undefined)
  const prepareRecoverySignIn = () => {
    if (options?.initialSavedAnalysis && options.oauthResumePath) return
    if (!recoveryId || !uploadedImageUrl || !isTrustedAnalysisImageUrl(uploadedImageUrl)) {
      throw new Error('This browser could not keep your photo for sign-in. You can still retry here.')
    }
    saveScanRecovery({
      id: recoveryId,
      savedAt: Date.now(),
      mode: analysisMode,
      reason: analysisSaveError ? 'save' : analysisServerError ? 'server' : analysisNetworkError ? 'network' : 'photo',
      imageUrl: uploadedImageUrl,
      supportingImageUrls: supportingImages.map((image) => image.url),
      zipCode,
      skipZip: skipZipStep,
      result: analysisResult,
      analysisId: savedAnalysisId,
      resultAccessToken,
      funnelId: funnelIdRef.current,
      attemptId: attemptIdRef.current,
      ...(isRefiningWithSupportingPhoto ? { refinement: {
        rootId: rescanOfAnalysisIdRef.current,
        trigger: refinementTriggerRef.current,
        guidance: supportingPhotoGuidance,
      } } : {}),
    })
  }

  // Clear diagnosis result (used when switching from diagnose → identify mode)
  const clearDiagnosisResult = useCallback(() => {
    setDiagnosisResult(null)
  }, [])

  // Clear identify result (used when switching from identify → diagnose mode,
  // e.g. the "diagnose this photo" cross-sell on the identify result screen)
  const clearAnalysisResult = useCallback(() => {
    setAnalysisResult(null)
    // Mirror resetUpload's teardown of the care-plan-success state. Without
    // this, the identify→"Diagnose this photo" cross-sell (and any later return
    // to an identify result) can flash a stale success card, and a leftover
    // carePlanNavigatedRef===true would make a subsequent email-gate submit
    // render the card but never auto-redirect, stranding the user.
    setEmailGateCompleted(false)
    setCompletedTimelineUrl(null)
    carePlanNavigatedRef.current = false
    if (carePlanRedirectTimerRef.current) {
      clearTimeout(carePlanRedirectTimerRef.current)
      carePlanRedirectTimerRef.current = null
    }
  }, [])

  const dismissReturnedFromGuide = useCallback(() => {
    setReturnedFromGuide(null)
  }, [])

  // A manual choice changes the canonical identity, never the model's score.
  // Preserve the original prediction for evaluation, clear model-authored
  // reasoning/tips that described another species, and let ResultsDisplay use
  // identitySource to unlock a user-selected plan without inventing 75%.
  //
  // "Model-authored" includes the photo evidence, the ranked candidate list and
  // the next-photo asks: all of them describe the species the model named, so
  // leaving them attached to the user's pick presents one species' evidence as
  // proof of another.
  const selectAlternative = useCallback((picked: string) => {
    // Read the model's original result BEFORE mutating, so the user's
    // correction is joinable to the model's guess — the ground-truth signal for
    // measuring ID accuracy. picker_context is inferred from the original so the
    // three picker entry points stay distinguishable without threading a prop.
    const previous = analysisResult
    setAnalysisResult((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        grassType: picked,
        alternatives: [],
        identitySource: 'user_selected',
        originalGrassType: prev.originalGrassType ?? prev.grassType,
        originalConfidence: prev.originalConfidence ?? prev.displayConfidence ?? prev.confidence,
        selectedAt: new Date().toISOString(),
        analysis: undefined,
        seasonalNotes: undefined,
        tips: [],
        enhancedTips: undefined,
        visibleEvidence: undefined,
        candidates: undefined,
        recovery: undefined,
        recommendedNextPhoto: undefined,
        missingEvidence: undefined,
      }
    })
    // Picker context derives from what the user SAW (display confidence).
    // Below 50 there was not enough model evidence for a strong correction
    // label; 50+ matches the native client's correction provenance contract.
    const previousEffective = previous
      ? (previous.displayConfidence ?? previous.confidence)
      : 0
    const pickerContext = !previous
      ? 'unknown'
      : previous.grassType === 'Unknown/Other'
        ? 'unknown_other'
        : previousEffective < 50
          ? 'low_confidence'
          : 'high_confidence_correction'
    const recoveryCandidates = previous?.recovery?.candidates
    const recoveryChoice = Array.isArray(recoveryCandidates)
      ? recoveryCandidates.find((candidate) => candidate && typeof candidate.name === 'string' && candidate.name === picked)
      : undefined
    trackEvent('unknown_other_alternative_picked', {
      picked,
      original_grass_type: previous?.grassType,
      original_confidence: previous?.confidence,
      picker_context: pickerContext,
      attempt_id: attemptIdRef.current ?? undefined,
      // Model attribution so correction rate (the accuracy KPI) slices by
      // model + prompt strategy in PostHog.
      model: (previous as { model?: string } | null)?.model,
      prompt_strategy: (previous as { promptStrategy?: string } | null)?.promptStrategy,
      analysis_id: savedAnalysisId ?? undefined,
      selection_source: 'user_selected',
      recovery_version: previous?.recovery?.version,
      recovery_status: previous?.recovery?.status,
      recovery_recommended: previous?.recovery?.recommendedGrass === picked,
      recovery_rank: recoveryChoice?.rank,
      recovery_candidate_source: recoveryChoice?.source,
    })
    // Ground-truth label for the grass-ID dataset: persist the correction onto
    // the analysis row. Fire-and-forget; labeling must never block the picker
    // UX. Skipped when this hook was bootstrapped from a saved-result page —
    // /result/[id] is a public share URL, and any same-NAT viewer would pass
    // the server's IP-hash ownership proof (share links are view-only for the
    // label pipeline; the `saved:` attempt-id prefix marks that cohort).
    const isSavedResultView = attemptIdRef.current?.startsWith('saved:') ?? false
    if (savedAnalysisId && !isSavedResultView) {
      const source = pickerContext === 'high_confidence_correction' ? 'corrected' : 'picker_fallback'
      void verifyAnalysis(savedAnalysisId, picked, source).then((ok) => {
        trackEvent('verified_label_write', { source, grass_type: picked, ok })
      })
    }
  }, [analysisResult, savedAnalysisId])

  const confirmIdentity = useCallback(() => {
    setAnalysisResult((current) => current
      ? { ...current, identitySource: 'user_confirmed' }
      : current)
  }, [])

  // Persist diagnosis state before OAuth redirect so state survives the round-trip
  const persistDiagnosisForOAuth = useCallback(() => {
    try {
      localStorage.setItem('pendingAnalysis_diagnosis', JSON.stringify({
        imageUrl: uploadedImageUrl,
        imageUrls: uploadedImageUrl
          ? [uploadedImageUrl, ...supportingImages.map((image) => image.url)]
          : undefined,
        zipCode,
        analysisMode: 'diagnose' as AnalysisMode,
        // Stash the anon analysis_history row id so the post-OAuth restore can
        // CLAIM it onto the new account (attaches it to /dashboard/history)
        // rather than forcing a re-run of the analysis.
        analysisId: savedAnalysisId ?? undefined,
        resultAccessToken: resultAccessToken ?? undefined,
        planType: diagnosisResult?.outcome === 'healthy' ? 'monitoring' : 'treatment',
        outcome: diagnosisResult?.outcome || 'problems',
        entryPath: 'diagnosis_result',
        subject: diagnosisResult?.problems?.[0]?.name || (diagnosisResult?.outcome === 'healthy' ? 'Healthy lawn' : undefined),
        savedAt: Date.now(),
      }))
    } catch { /* localStorage unavailable */ }
  }, [diagnosisResult, uploadedImageUrl, supportingImages, zipCode, savedAnalysisId, resultAccessToken])

  return {
    // Image state
    uploadedImage,
    uploadedImageFile,
    uploadedImageUrl,
    isUploading,
    uploadError,
    supportingImages,
    isUploadingSupportingImages,
    supportingImageError,
    isRefiningWithSupportingPhoto,
    supportingPhotoGuidance,
    photoQuality,
    supportingPhotoQuality,
    photoCap,
    supportingPhotoSlots,

    // Refinement state
    refinementPolicy,
    refinementSummary,
    photoCapGate,
    dismissPhotoCapGate,
    handleRefinementGateShown,
requestSupportingPhotoSignIn,

    // Mode state
    analysisMode,
    setAnalysisMode,

    // Analysis state
    isAnalyzing,
    analysisResult,
    diagnosisResult,
    savedAnalysisId,
    resultAccessToken,
    recoveredWeed,
    isRecoveringWeed,
    weedRecoveryFailed,
    funnelId: funnelIdRef.current,
    attemptId: attemptIdRef.current,
    originLawnId:
      options?.initialSavedAnalysis?.lawnId
      ?? options?.lawnContext?.lawnId
      ?? null,
    selectedLawnId: analysisLawnContextRef.current.lawnId ?? null,
    limitReached,
    limitTier,
    upgradeRequired,
    analysisNetworkError,
    analysisSaveError,
    analysisServerError,

    // ZIP code state
    zipCode,
    zipCodeError,
    isZipCodeValid,
    skipZipStep,

    // Email gate state
    carePlanEmail,
    carePlanEmailError,
    emailSuggestion,
    isSubmittingCarePlan,
    emailGateCompleted,
    completedTimelineUrl,
    timelineRedirectUrl,
    oauthResumeUrl,
    recoveryRedirectUrl,
    prepareRecoverySignIn,
    navigateToCompletedTimeline,

    // UI state
    returnedFromGuide,
    dismissReturnedFromGuide,

    // Auth
    user,
    authLoading,
    // Entitlement for the result surfaces' paid continuation. Exposed here so
    // the result components stay provider-free and testable without an
    // AuthProvider; they already receive `user` the same way.
    authIsPremium,
    subscriptionLoading,
    router,

    // Handlers
    handleImageUpload,
    handleSupportingImagesUpload,
    removeSupportingImage,
    analyzeImage,
    recoverIdentifyAsWeed,
    retryAnalysis,
    resetUpload,
    retryWithNewPhoto,
    refineWithSupportingPhoto,
    clearDiagnosisResult,
    clearAnalysisResult,
    persistDiagnosisForOAuth,
    handleZipCodeChange,
    handleZipCodeKeyPress,
    handleCarePlanEmailChange,
    handleAcceptEmailSuggestion,
    handleDismissEmailSuggestion,
    handleCarePlanEmailKeyPress,
    handleCarePlanSubmit,
    handleResultPrimaryCtaClick,
    scanUsage,
    anonQuotaExhausted,
    anonQuotaReason,
    handleScanQuotaWarningShown,
    handleScanQuotaWarningClicked,
    handleImpermanenceNoteClick,
    handleSaveAndViewTimeline,
    selectAlternative,
    confirmIdentity,
  }
}
